import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAppData } from '@/app-data';
import { useDocumentTitle } from '@/utils/use-document-title';
import { useIdentity } from '@/features/identity/identity';
import * as api from '@/features/backlog/api';
import { Item, Member, Priority, Status, StatusMetric, TaskType } from '@/features/backlog/types';
import { formatDays } from '@/features/backlog/status-timeline';
import { PRIORITIES, TASK_TYPES, priorityMeta, taskTypeMeta, STATUS_PALETTE, hiddenFromBoard } from '@/features/backlog/constants';
import { StatusMeta, useStatuses, useStatusMeta } from '@/features/backlog/statuses';
import { useEpics, useEpicDropTarget, epicFocusMatches, focusedEpicForCreate } from '@/features/backlog/epics';
import { useHistory } from '@/features/history/history';
import { existenceCommand, placementCommand, removalCommand, snapshotCommand } from '@/features/history/commands';
import { useLiveItems } from '@/features/realtime/realtime';
import { useFilters } from '@/features/backlog/use-filters';
import { filtersActive, matchesFilters } from '@/features/backlog/filter';
import { fmtDate } from '@/features/backlog/format';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inputCls } from '@/components/ui/form';
import { cn } from '@/components/ui/cn';
import { DatePicker } from '@/components/ui/DatePicker';
import { Popover, menuRow } from '@/components/ui/Popover';
import { SelectOption, SelectPopover } from '@/components/ui/SelectPopover';
import { ContextMenu, MenuEntry, useContextMenu } from '@/components/ui/ContextMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ColorPicker } from '@/components/ui/ColorPicker';
import {
  ArrowRight,
  Calendar,
  Chevron,
  Comment,
  Duplicate,
  ExternalLink,
  InsertAbove,
  InsertBelow,
  Link,
  MoreVertical,
  OpenPanel,
  Pencil,
  Plus,
  PriorityChevron,
  Promote,
  SubItem,
  TaskTypeGlyph,
  ToBottom,
  ToTop,
  Trash,
} from '@/components/ui/icons';
import { taskPath } from '@/constants/routes';
import MemberAvatar from '@/components/member-avatar';
import StatusSwatch from '@/components/status-dot';
import { useMemberCreate } from '@/components/new-member';
import EpicChip from '@/components/epic-chip';
import FilterBar from '@/components/filter-bar';
import CollapseExpand from '@/components/collapse-expand';
import ItemDrawer, { ItemTab } from '@/components/item-drawer';
import { SELECTED_CLS, useItemPanel } from '@/components/item-panel';
import { ancestorIds, useItemUrlSync } from '@/features/backlog/use-item-url';
import SavedViews from '@/components/saved-views';
import { subtreeIds, visibleSelectedId } from '@/pages/board/tree';
import { duplicateItem } from '@/features/backlog/duplicate';

const stop = (e: ReactPointerEvent) => e.stopPropagation(); // keep editors/buttons from starting a card drag

// The whole card is the drag handle, so on TOUCH a plain move would start a drag and fight scrolling.
// This sensor keeps the mouse behaviour (drag after a 5px move) but requires an explicit press-and-hold
// on touch: the drag only arms after holding ~220ms roughly in place — any earlier finger movement
// (i.e. a scroll) cancels it, so the board scrolls normally. It stays on pointer events, so every
// `onPointerDown={stop}` guard on inner controls keeps working unchanged.
type PointerSensorCtorProps = ConstructorParameters<typeof PointerSensor>[0];
class TouchHoldPointerSensor extends PointerSensor {
  constructor(props: PointerSensorCtorProps) {
    const isTouch = (props.event as PointerEvent).pointerType === 'touch';
    super({
      ...props,
      options: {
        ...props.options,
        activationConstraint: isTouch ? { delay: 220, tolerance: 8 } : { distance: 5 },
      },
    });
  }
}

// Nest vs reorder is decided by WHERE on a card the pointer sits: within REORDER_EDGE_PX of the top
// or bottom edge (or in the gap between cards) it REORDERS, showing a blue divider at that slot;
// anywhere else on the card it NESTS (the whole card highlights). A fixed pixel band keeps the
// reorder zone a consistent sliver near the boundary regardless of card height (a tall expanded
// card is still mostly a nest target). Immediate — no dwell timer. See collisionDetection.
const REORDER_EDGE_PX = 30;

// Epics and initiatives are managed through their own dedicated UI (the epic sidebar), not the
// board grid, so the kanban view never renders them as cards. They stay in `items` (card chips
// still resolve their epic via itemsById) but are excluded from the rendered tree below. A card
// parented to one is promoted to the top level rather than disappearing with its hidden parent.

type EditTarget = { id: string; field: string } | null;

export default function KanbanPage() {
  const { currentBoard, members, loading: appLoading } = useAppData();
  useDocumentTitle(currentBoard?.name);
  const { filters, setFilters } = useFilters();
  const { record, dataVersion, showToast } = useHistory();
  const { statuses, valuesKey, defaultValue: defaultStatus, reload: reloadStatuses } = useStatuses();
  const metaOf = useStatusMeta();
  const { me } = useIdentity();
  const boardId = currentBoard?.id ?? null;

  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditTarget>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // View-only overlay: ancestors force-opened to reveal a deep-linked (`?item=`) nested card. Kept OUT
  // of the persisted `expanded` set (mirrors `searchExpanded`), so a shared link never syncs an
  // unwanted expansion across the user's devices. Unioned into `searchExpanded` for rendering below.
  const [deepLinkExpanded, setDeepLinkExpanded] = useState<Set<string>>(new Set());
  // Mirror of `expanded` so a toggle can compute the next set (and kick off the persisting DB
  // write) OUTSIDE a state updater — StrictMode double-invokes updaters in dev, and a write in
  // there would fire twice (same reasoning as the details panel's pin toggle).
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  // Persisted per-member, per-board card expansion. `prefsMapRef` is the live, authoritative
  // board→openIds map (loaded once, then patched on every save) and is what hydration reads,
  // so switching boards and back restores this session's toggles, not just the initial load.
  // `prefsLoaded` only signals "the fetch has landed" to trigger hydration; `hydratedBoardRef`
  // marks the board we've already applied, so a re-render (or a live toggle) never re-hydrates
  // and clobbers the user's current expansion.
  const prefsMapRef = useRef<Record<string, string[]>>({});
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const hydratedBoardRef = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Boards the user has toggled during THIS load cycle. If a toggle lands before the prefs GET
  // resolves (items can render first), that board's fresh in-session value must win over the
  // arriving server set — otherwise the reconcile would snap the just-opened card shut and
  // persist the stale set. Same last-writer guard the details-panel pin uses (userTouchedRef).
  const touchedRef = useRef<Set<string>>(new Set());
  // App-wide details panel (see ItemPanelProvider) — stays docked across view switches and
  // supplies the single "selected item" the cards highlight.
  const panel = useItemPanel();
  const { focusedEpicId, epics: epicList, draggingEpicId } = useEpics();
  // The epic a new card should adopt from the current focus (null when focused on nothing / "Unassigned").
  const focusEpicForNew = focusedEpicForCreate(focusedEpicId);
  const [confirmDel, setConfirmDel] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Column (status) management: which column's name is being inline-edited, the pending
  // delete awaiting confirmation, and whether a status write is in flight (dims the board's
  // "Saving…" cue and disables the delete confirm).
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [confirmDelStatus, setConfirmDelStatus] = useState<Status | null>(null);
  // Per-column average cycle time (avg days the items now in each status have been there). Fetched
  // from the server (reconstructed from status-change history) and refreshed as items change.
  const [metricByStatus, setMetricByStatus] = useState<Record<string, StatusMetric>>({});
  const [statusBusy, setStatusBusy] = useState(false);
  // The board scrolls inside its own container (below), so the app header and the FilterBar stay
  // put — only the columns move, on both axes. `scrolled` tracks that container's vertical offset
  // so the under-header fade only appears once cards actually pass under a pinned column header
  // (at the very top it would sit over the first card's insert/edit controls).
  const boardRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const onBoardScroll = () => {
    const el = boardRef.current;
    const s = !!el && el.scrollTop > 0;
    setScrolled((p) => (p !== s ? s : p));
  };
  // Live mirror of the drag state for the touch handlers below (which are attached once, outside React).
  const activeIdRef = useRef<string | null>(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  // MOBILE horizontal-swipe guard. The board's touch-action is `pan-y` on mobile (see boardCls), so the
  // browser only ever scrolls it VERTICALLY by touch — a vertical card-scroll can no longer drift
  // sideways into the next column (the reported problem), and vertical momentum is untouched. Changing
  // column is now an intentional gesture: a clearly-horizontal swipe steps one column left/right, snapped
  // via a programmatic scroll (unaffected by touch-action). A card drag (activeId) is never treated as a
  // swipe. Desktop (≥ sm) keeps native 2-axis scrolling — this handler no-ops there.
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const mobile = window.matchMedia('(max-width: 639px)');
    let startX = 0;
    let startY = 0;
    let tracking = false;
    let hadDrag = false;
    const stepColumn = (dir: 1 | -1) => {
      const cols = Array.from(el.querySelectorAll<HTMLElement>('[data-snap-col]'));
      if (cols.length < 2) return;
      const pad = 24; // scroll-padding-left (scroll-pl-6)
      const start = el.getBoundingClientRect().left + pad;
      let current = 0;
      let best = Infinity;
      cols.forEach((c, i) => {
        const d = Math.abs(c.getBoundingClientRect().left - start);
        if (d < best) { best = d; current = i; }
      });
      const target = Math.max(0, Math.min(cols.length - 1, current + dir));
      if (target === current) return;
      // Horizontal-only scroll (preserve scrollTop) so switching columns never jumps the vertical position.
      const delta = cols[target].getBoundingClientRect().left - start;
      el.scrollBy({ left: delta, top: 0, behavior: 'smooth' });
    };
    const onStart = (e: TouchEvent) => {
      if (!mobile.matches || e.touches.length !== 1) { tracking = false; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      hadDrag = !!activeIdRef.current;
      tracking = true;
    };
    const onMove = () => { if (tracking && activeIdRef.current) hadDrag = true; };
    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      if (hadDrag || activeIdRef.current) return; // a card drag, not a swipe
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Decisive, clearly-horizontal swipe only (so vertical scrolls / taps never step a column).
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      stepColumn(dx < 0 ? 1 : -1);
    };
    const onCancel = () => { tracking = false; };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, []);
  // The dragged card's status+position+parent at pick-up. A drop that didn't move cancels
  // (restoring all three); every real drop reads these as the "before" for the undo record and
  // the no-op checks. parentId lets a cancelled drag restore the card under its old parent.
  const dragOrig = useRef<{ status: string; position: number; parentId: string | null } | null>(null);
  // A TOP-LEVEL card's live status+position as (re)placed by onDragOver during a cross-column
  // drag. When a drop lands at the top of the new column the card's OWN slot sits under the
  // pointer, so `over` resolves to itself; this lets onDragEnd commit that move instead of
  // cancelling it. Children never set this — they aren't mutated mid-drag (see onDragOver).
  const dragLive = useRef<{ status: string; position: number } | null>(null);
  // ---- nest / reorder drag state ----
  // `armedRef` is true while the pointer sits in a REORDER zone (a card's top/bottom edge or a
  // gap): the drop then places the dragged card as a sibling of `overCardRef` at `insertionRef`,
  // and a blue divider marks the slot. Otherwise the drop NESTS. `hoverNestRef` is true over an
  // explicit sub-items strip (always nest). `geomRef` is the latest hover geometry the collision
  // computed (over card, its list/index, which side, and nest-vs-reorder), read by onDragMove.
  const armedRef = useRef(false);
  const overCardRef = useRef<string | null>(null);
  // The reorder slot (list + index + which card + side) the divider marks; used at drop.
  const insertionRef = useRef<{ listKey: string; index: number; overId: string; before: boolean } | null>(null);
  const hoverNestRef = useRef(false);
  const geomRef = useRef<{ overId: string | null; overNest: boolean; listKey: string | null; index: number; before: boolean; reorder: boolean } | null>(null);
  // Where the blue "reorder here" divider renders: which list (`col:<status>` / `kids:<id>`) and
  // the index within it. `dwellLineRef` mirrors it so onDragMove only re-renders on a real change.
  const [dwellLine, setDwellLine] = useState<{ listKey: string; index: number } | null>(null);
  const dwellLineRef = useRef<{ listKey: string; index: number } | null>(null);
  // The card the drop would NEST into (highlighted whole-card while dragging over its middle).
  // `nestTargetRef` mirrors it so onDragMove only re-renders on an actual change.
  const [nestTargetId, setNestTargetId] = useState<string | null>(null);
  const nestTargetRef = useRef<string | null>(null);
  // The status column the cursor is over WHEN it differs from the dragged card's own column — the
  // whole column is highlighted (see Column `dropTarget`) so moving a card across columns reads as a
  // deliberate change. `overColRef` mirrors it (re-render only on change); `withinColRef` is the raw
  // cursor column that collisionDetection publishes for onDragMove to compare against the origin.
  const [overColId, setOverColId] = useState<string | null>(null);
  const overColRef = useRef<string | null>(null);
  const withinColRef = useRef<string | null>(null);
  // True for the single click the browser emits when a drag ends on the same card, so a
  // reorder never also opens the drawer. Reset at the start of every fresh pointer press.
  const dragClick = useRef(false);
  // Guards against re-triggering a deep duplicate while its (serial) creates are still in flight.
  const duplicatingRef = useRef(false);
  // Re-entry guard for creating a column, so a fast double-click can't create two.
  const addingColumnRef = useRef(false);

  // Dragging stays enabled even while a search/filter narrows the board: the drop math runs against
  // the VISIBLE lists (see `viewList`), so a reorder positions a card relative to the ranks of its
  // shown neighbours rather than the full set — no misplacement, and a move inside a filtered view
  // leaves the global order of everything hidden untouched.
  const sensors = useSensors(useSensor(TouchHoldPointerSensor));

  const reload = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const res = await api.listItems({
        limit: 2000,
        filter: [{ name: 'board_id', op: 'eq', value: boardId }],
        sort: [{ name: 'position', dir: 'asc' }],
      });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  // dataVersion bumps after an undo/redo/seek so the view reloads from server truth;
  // valuesKey changes when a status is added/removed (a delete moved items) → refetch.
  useEffect(() => {
    reload();
  }, [reload, dataVersion, valuesKey]);

  // Apply other people's edits live (create/update/move/delete/restore) as they happen.
  useLiveItems(boardId, setItems, reload);

  // Load the per-column average cycle time, debounced so a burst of edits/moves coalesces into one
  // refresh. Re-runs when the board changes, on undo/redo, and as the items change (e.g. a drop).
  useEffect(() => {
    if (!boardId) {
      setMetricByStatus({});
      return undefined;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      api
        .boardStatusMetrics(boardId)
        .then((ms) => {
          if (cancelled) return;
          const map: Record<string, StatusMetric> = {};
          for (const m of ms) map[m.status] = m;
          setMetricByStatus(map);
        })
        .catch(() => undefined);
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [boardId, dataVersion, items]);

  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // Mirror the open card into a shareable `?item=<key>` URL, and open + center a card a link arrives
  // on. `revealItem` force-opens a deep-linked card's ancestors (the Kanban hides children until a card
  // is expanded) so it lands on the card itself, not the collapsed parent it was tucked inside — into
  // the view-only overlay, never the persisted `expanded` set (see `deepLinkExpanded`).
  const revealItem = useCallback(
    (id: string) => {
      const anc = ancestorIds(id, itemsById);
      if (anc.size === 0) return;
      setDeepLinkExpanded((prev) => {
        if ([...anc].every((a) => prev.has(a))) return prev;
        return new Set([...prev, ...anc]);
      });
    },
    [itemsById],
  );
  useItemUrlSync(items, { revealItem });
  // Resolve a card's epic (for its chip) from the EPIC LIST the sidebar owns — the EpicsProvider keeps
  // it live via its own item feed, so an epic created inline (whose realtime echo `useLiveItems` skips
  // in this tab) still resolves the instant it's dropped onto a card, even though the board's own
  // `items` won't carry it until the next full reload. Fall back to the view's items.
  const epicById = useMemo(() => new Map(epicList.map((e) => [e.id, e])), [epicList]);
  const epicOf = useCallback(
    (epicId: string | null) => (epicId ? epicById.get(epicId) ?? itemsById.get(epicId) : undefined),
    [epicById, itemsById],
  );

  // A card's parent AS RENDERED: its parent_id, unless that parent is hidden from the board (an
  // epic/initiative) or absent — in which case the card is promoted to top level, exactly as
  // cols/childrenBy place it. Everything that reasons about a card's rendered list / column /
  // ancestry (listOf, columnOf, the count walk) MUST use this rather than raw parent_id, or it
  // disagrees with what's on screen — e.g. a drag would reparent a card under a hidden epic.
  const renderParent = (it: Item): Item | undefined => {
    const p = it.parent_id ? itemsById.get(it.parent_id) : undefined;
    return p && !hiddenFromBoard(p) ? p : undefined;
  };
  // The top-level card `it` renders under (itself if already top level), walking only through
  // non-hidden parents. Bounded + cycle-safe (a stray parent_id loop must never spin forever).
  const renderTop = (it: Item): Item => {
    let cur = it;
    for (let i = 0; i < 10000; i++) {
      const p = renderParent(cur);
      if (!p) break;
      cur = p;
    }
    return cur;
  };

  const activeMembers = useMemo(
    () => [...members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );
  // While a card is being dragged, the ids of it + its descendants — a card's nest drop zone
  // disables itself when its card is in here (you can't nest a card under itself or its own child).
  const activeSubtree = useMemo(() => (activeId ? subtreeIds(items, activeId) : null), [activeId, items]);

  // direct children per parent (sorted), for the nested cards. Hidden-type items (epics/initiatives)
  // are never cards, so they are neither listed as children nor allowed to hold children — a card
  // under a hidden parent is dropped here and instead surfaces as top-level in `cols` below.
  const childrenBy = useMemo(() => {
    const m = new Map<string, Item[]>();
    const byId = new Map(items.map((i) => [i.id, i]));
    for (const it of items) {
      if (hiddenFromBoard(it)) continue;
      const parent = it.parent_id ? byId.get(it.parent_id) : undefined;
      if (parent && !hiddenFromBoard(parent)) {
        const arr = m.get(parent.id) ?? [];
        arr.push(it);
        m.set(parent.id, arr);
      }
    }
    for (const arr of m.values()) arr.sort((a, b) => a.position - b.position);
    return m;
  }, [items]);

  // columns hold ONLY top-level items; children live inside their parent card. A column exists
  // per board status; items whose status isn't a known column (briefly, right after a column is
  // deleted elsewhere) fall through `?? []` into no column until the reassignment reload lands.
  const cols = useMemo(() => {
    const map: Record<string, Item[]> = {};
    for (const s of statuses) map[s.value] = [];
    const byId = new Map(items.map((i) => [i.id, i]));
    for (const it of items) {
      if (hiddenFromBoard(it)) continue; // epics/initiatives have their own UI, never a column card
      const parent = it.parent_id ? byId.get(it.parent_id) : undefined;
      // Top-level when there's no present parent, or the parent is itself hidden (promote the card
      // rather than lose it with the epic/initiative it hangs under).
      const top = !parent || hiddenFromBoard(parent);
      if (top && map[it.status]) map[it.status].push(it);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.position - b.position);
    return map;
  }, [items, statuses]);

  // ---- persisted card expansion (which cards are opened to reveal their sub-items) ----
  // Load this member's saved expansion once their identity is known; a fresh identity re-hydrates
  // from its own prefs. The DB is the source of truth — a failed load just leaves the board
  // collapsed. (The details-panel pin reads the same blob independently; they write distinct
  // top-level keys, so the server's shallow merge never lets the two clobber each other.)
  useEffect(() => {
    let cancelled = false;
    // Drop the outgoing member's session state so nothing of theirs is applied to — or, via a
    // still-armed debounce, persisted under — the incoming member. `saveMyPreferences` writes for
    // whoever is authenticated when it fires, so a pending timer must be cancelled here, not left
    // to land under the new actor. (No-op on first mount: the map is empty and no timer is armed.)
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    prefsMapRef.current = {};
    hydratedBoardRef.current = null;
    touchedRef.current = new Set();
    setPrefsLoaded(false);
    api
      .getMyPreferences()
      .then((p) => {
        if (cancelled) return;
        // Overlay the server set with any board the user already toggled this cycle, so a toggle
        // made while this GET was in flight isn't discarded (nor collapsed by the hydration below,
        // which reads this same map). Untouched boards adopt server truth.
        const map = { ...(p.kanban_expanded ?? {}) };
        for (const b of touchedRef.current) map[b] = prefsMapRef.current[b] ?? [];
        prefsMapRef.current = map;
        setPrefsLoaded(true);
      })
      .catch(() => {
        /* keep everything collapsed */
      });
    return () => {
      cancelled = true;
    };
  }, [me?.id]);

  // Apply the saved open cards for the current board — once the prefs have loaded and once per
  // board (expansion is tracked per board). Reads the live map so switching boards restores that
  // board's own open set (including this session's toggles) rather than carrying the previous
  // board's over, and the `hydratedBoardRef` guard stops it re-running over a fresh toggle.
  useEffect(() => {
    if (!prefsLoaded || !boardId || hydratedBoardRef.current === boardId) return;
    setExpanded(new Set(prefsMapRef.current[boardId] ?? []));
    hydratedBoardRef.current = boardId;
  }, [prefsLoaded, boardId]);

  // Persist the given open set for the current board, debounced to coalesce a burst of toggles.
  // Drops ids whose items no longer exist so deleted cards don't linger in the blob. Sends the
  // whole board→openIds map because the server merge overlays only top-level keys (a per-board
  // sub-key can't be patched in isolation), and we hold that full map in `prefsMapRef`.
  const persistExpanded = (next: Set<string>) => {
    if (!boardId) return;
    touchedRef.current.add(boardId);
    const ids = [...next].filter((id) => itemsById.has(id));
    prefsMapRef.current = { ...prefsMapRef.current, [boardId]: ids };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      api.saveMyPreferences({ kanban_expanded: prefsMapRef.current }).catch(() => {
        /* best-effort; the next successful write carries it */
      });
    }, 500);
  };

  // Flush a still-pending save if the view unmounts before the debounce fires (e.g. a quick view
  // switch right after toggling). No-op when nothing is queued.
  useEffect(
    () => () => {
      if (!saveTimer.current) return;
      clearTimeout(saveTimer.current);
      api.saveMyPreferences({ kanban_expanded: prefsMapRef.current }).catch(() => {});
    },
    [],
  );

  // ---- filtering / search (in-place highlight, context-preserving) ----
  // A search/filter no longer flattens the board. It keeps the normal nested layout but narrows it
  // to the MATCHING BRANCHES: a match is lifted (full strength + an amber ring); its parent chain
  // and its siblings stay visible but DIMMED so the result reads in context; every unrelated branch
  // (a parent with no matching descendant) is HIDDEN outright rather than dimmed. Drag is disabled
  // (a read-only result view) while inline field edits keep working. Derived sets below:
  //   matchIds   — the cards that match (null when nothing is active)
  //   branchIds  — matches + their ancestors (the branches worth keeping)
  //   visibleIds — branchIds + the direct children of a branch card (a match's siblings) = renders
  const active = filtersActive(filters);
  const matchIds = useMemo(() => {
    if (!active) return null;
    const s = new Set<string>();
    for (const it of items) if (matchesFilters(it, filters, memberMap)) s.add(it.id);
    return s;
  }, [active, items, filters, memberMap]);

  // Cards ON a matching branch: a match itself, or an ANCESTOR of one (its subtree contains a
  // match). These are the cards worth keeping — a parent with no matching descendant is dropped
  // entirely rather than dimmed, so unrelated branches don't clutter the result.
  const branchIds = useMemo(() => {
    if (!matchIds) return null;
    const s = new Set(matchIds);
    for (const id of matchIds) {
      // Walk to the root adding each ancestor (bounded + cycle-safe, mirroring columnOf).
      let it = itemsById.get(id);
      for (let i = 0; i < 10000 && it?.parent_id && itemsById.has(it.parent_id); i++) {
        it = itemsById.get(it.parent_id)!;
        s.add(it.id);
      }
    }
    return s;
  }, [matchIds, itemsById]);

  // What actually RENDERS while searching: every branch card (match or ancestor), PLUS the direct
  // siblings of a match — the other children of a match's parent, kept (dimmed) so a result reads
  // beside its neighbours. Everything else is hidden: an unrelated parent (including an ANCESTOR's
  // other children — an "uncle" with no matching descendant) is dropped, and a top-level match has
  // no parent so its top-level neighbours stay hidden too ("hide parents with no matching child").
  const visibleIds = useMemo(() => {
    if (!branchIds || !matchIds) return null;
    const s = new Set(branchIds);
    // The parents whose full child list we keep: the direct (present) parent of each match.
    const matchParents = new Set<string>();
    for (const id of matchIds) {
      const p = itemsById.get(id)?.parent_id;
      if (p && itemsById.has(p)) matchParents.add(p);
    }
    for (const it of items) {
      if (it.parent_id && matchParents.has(it.parent_id)) s.add(it.id);
    }
    return s;
  }, [branchIds, matchIds, items, itemsById]);

  // ---- epic focus narrowing (click an epic chip in the sidebar) ----
  // Focusing an epic narrows the board to that epic's BRANCHES instead of merely dimming the rest:
  // a card LINKED to the epic stays (at full strength), as do its ANCESTORS (so a nested epic item
  // is reachable — dimmed, for context) and its own SUBTREE (its sub-items, kept so the linked card
  // reads with its children). Every other branch — a parent that is neither in the epic nor has an
  // epic descendant — is HIDDEN outright. Keyed off epic_id membership; unlike search it carries no
  // amber match ring and no drag lock, so the epic drag-to-link and inline edits keep working.
  const epicMatchIds = useMemo(() => {
    if (!focusedEpicId) return null;
    const s = new Set<string>();
    for (const it of items) if (epicFocusMatches(it.epic_id, focusedEpicId)) s.add(it.id);
    return s;
  }, [focusedEpicId, items]);
  const epicBranchIds = useMemo(() => {
    if (!epicMatchIds) return null;
    const s = new Set(epicMatchIds);
    for (const id of epicMatchIds) {
      // Ancestors, so a nested epic item is reachable (bounded + cycle-safe, mirroring columnOf).
      let it = itemsById.get(id);
      for (let i = 0; i < 10000 && it?.parent_id && itemsById.has(it.parent_id); i++) {
        it = itemsById.get(it.parent_id)!;
        s.add(it.id);
      }
      // The linked card's own subtree, so its sub-items stay available under it.
      for (const d of subtreeIds(items, id)) s.add(d);
    }
    return s;
  }, [epicMatchIds, items, itemsById]);

  // Reveal matches that sit inside collapsed parents by force-opening the ANCESTORS of every match
  // — a view-only overlay on the user's own expansion (never persisted, so clearing the search
  // snaps back to exactly what they had open). EVERY ancestor is opened, INCLUDING one that is
  // itself a match, so a match nested under another (collapsed) matching card is still revealed —
  // parents start collapsed, so that case is the default, not an edge. A match's OWN subtree is
  // left as the user had it. Identity-equal to `expanded` when idle (nothing downstream changes).
  const searchExpanded = useMemo(() => {
    // Force-open the ancestors of every search match AND every epic-focus match, so a result (or a
    // nested epic item) tucked inside a collapsed parent is revealed rather than hidden away.
    const revealFrom = new Set<string>();
    if (matchIds) for (const id of matchIds) revealFrom.add(id);
    if (epicMatchIds) for (const id of epicMatchIds) revealFrom.add(id);
    if (revealFrom.size === 0 && deepLinkExpanded.size === 0) return expanded;
    const next = new Set(expanded);
    // Deep-link reveal overlay: its ids are already the ancestors to open (see revealItem).
    for (const id of deepLinkExpanded) next.add(id);
    for (const id of revealFrom) {
      // Walk to the root opening each ancestor (bounded + cycle-safe, mirroring columnOf).
      let it = itemsById.get(id);
      for (let i = 0; i < 10000 && it?.parent_id && itemsById.has(it.parent_id); i++) {
        it = itemsById.get(it.parent_id)!;
        next.add(it.id);
      }
    }
    return next;
  }, [matchIds, epicMatchIds, expanded, itemsById, deepLinkExpanded]);

  // The set of cards that actually RENDER: the search narrowing and the epic-focus narrowing,
  // intersected when both are active (a card must survive both), or whichever is active alone; null
  // when neither is (everything renders). Fed to the card lists as `visibleIds`.
  const shownIds = useMemo(() => {
    if (visibleIds && epicBranchIds) {
      const s = new Set<string>();
      for (const id of visibleIds) if (epicBranchIds.has(id)) s.add(id);
      return s;
    }
    return visibleIds ?? epicBranchIds;
  }, [visibleIds, epicBranchIds]);

  // Narrow a sibling list to just the cards the current filter SHOWS, so a reorder — drag or the
  // menu's bring-to-top/bottom — positions a card relative to the RANKS OF ITS VISIBLE NEIGHBOURS,
  // never hidden ones. That keeps a move made inside a filtered view from shuffling the global order
  // of everything hidden. A no-op (returns the list as-is) when nothing is filtered.
  const viewList = (list: Item[]) => (shownIds ? list.filter((i) => shownIds.has(i.id)) : list);

  // Collision-safe rank for slotting a card between its VISIBLE neighbours `before`/`after` within a
  // (possibly) filtered list: it lands in the gap right beside that visible neighbour in the FULL
  // list, so it never ties or leapfrogs a hidden card — which a plain before±1 would when hidden
  // cards sit within 1.0 of the visible edge. `full` is the sibling list WITHOUT the moving card,
  // sorted by position. Identical to the midpoint / ±1 rule when nothing is filtered (full == visible).
  const gapPosition = (full: Item[], before: Item | undefined, after: Item | undefined): number => {
    if (!before && !after) return 1000;
    if (!before) {
      // Top of the visible list: sit just above `after`, in the gap below whatever full-list card
      // precedes it (hidden or not) — or a plain −1 when `after` is also the full-list head.
      const ai = full.findIndex((i) => i.id === after!.id);
      const prev = ai > 0 ? full[ai - 1] : undefined;
      return prev ? (prev.position + after!.position) / 2 : after!.position - 1;
    }
    // Otherwise sit just below `before`, in the gap above whatever full-list card follows it.
    const bi = full.findIndex((i) => i.id === before.id);
    const next = bi >= 0 ? full[bi + 1] : undefined;
    return next ? (before.position + next.position) / 2 : before.position + 1;
  };

  // Per-column tally of the cards driving the current narrowing — SEARCH matches, or (when an epic is
  // focused) the cards linked to it — each counted under the column it RENDERS in (its top-level
  // ancestor's status), and only if it survives a co-active filter (shownIds, for search ∩ epic).
  // Drives the header count badge so it reflects the filtered view, not the full column total. Null
  // when nothing is filtered (the header then shows the plain full total).
  const filteredCountByStatus = useMemo(() => {
    const primary = matchIds ?? epicMatchIds;
    if (!primary) return null;
    const counts: Record<string, number> = {};
    for (const s of statuses) counts[s.value] = 0;
    for (const id of primary) {
      if (shownIds && !shownIds.has(id)) continue; // a card hidden by the OTHER filter doesn't count
      const it = itemsById.get(id);
      if (!it) continue;
      // Count under the column the card RENDERS in: its render-top ancestor's status (which, for a
      // card promoted out of a hidden epic, is its own status — where cols places it).
      const top = renderTop(it);
      if (counts[top.status] != null) counts[top.status]++;
    }
    return counts;
  }, [matchIds, epicMatchIds, shownIds, statuses, itemsById]);

  const statusOf = (id: string): string | null => {
    if (id.startsWith('col:')) return id.slice(4);
    return itemsById.get(id)?.status ?? null;
  };

  // The column an id visually lives in: a column droppable maps to its own status; a card maps
  // to its TOP-LEVEL ancestor's status (children render nested inside that ancestor's column,
  // even when their own status differs). Used by the collision detection to tell "same column"
  // (precise card slotting) from "another column" (cross-column / pop-out).
  const columnOf = (id: string): string | null => {
    if (id.startsWith('col:')) return id.slice(4);
    const it = itemsById.get(id.startsWith('nest:') ? id.slice(5) : id);
    if (!it) return null;
    // The RENDER top-level ancestor's status — walking only through non-hidden parents, so a card
    // under a hidden epic maps to its OWN column (where cols promotes it), not the epic's. renderTop
    // is bounded + cycle-safe (this runs on every pointer move during a drag).
    return renderTop(it).status;
  };

  // A child card is one whose parent is present on the board (so it renders nested,
  // not in a column). Reordering a child is scoped to its siblings.
  const parentOf = (id: string): string | null => {
    const it = itemsById.get(id);
    return it?.parent_id && itemsById.has(it.parent_id) ? it.parent_id : null;
  };

  const saveField = async (item: Item, patch: Partial<Item>) => {
    const merged = { ...item, ...patch };
    setItems((cur) => cur.map((i) => (i.id === item.id ? merged : i)));
    try {
      const saved = await api.updateItem(item.id, {
        board_id: merged.board_id,
        parent_id: merged.parent_id,
        epic_id: merged.epic_id,
        title: merged.title,
        description: merged.description,
        status: merged.status,
        priority: merged.priority,
        type: merged.type,
        assignee_id: merged.assignee_id,
        pdm_id: merged.pdm_id,
        source: merged.source,
        start_date: merged.start_date,
        end_date: merged.end_date,
      });
      setItems((cur) => cur.map((i) => (i.id === item.id ? saved : i)));
      record(snapshotCommand(item, saved, `Edit ${item.key}`));
    } catch {
      reload();
    }
  };

  // Link an item to an epic dropped from the sidebar, cascading to its whole subtree so every
  // nested subtask picks up the same epic (narrow write — only epic_id changes, so it can't clobber
  // a concurrent field edit). The subtree is computed from the freshest state inside the updater;
  // the server cascades the same set atomically. Optimistic; a failure reloads from server truth.
  const linkEpic = useCallback(
    (itemId: string, epicId: string) => {
      if (itemId === epicId) return; // an epic can't belong to itself
      setItems((cur) => {
        const ids = subtreeIds(cur, itemId);
        // Mirror the server cascade: (re)link the drop target itself, and cascade only to subtasks
        // that have NO epic yet — a subtask already on another epic is a deliberate choice we leave
        // alone — and never to a nested epic.
        return cur.map((i) =>
          ids.has(i.id) && i.id !== epicId && (i.id === itemId || (i.type !== 'epic' && !i.epic_id))
            ? { ...i, epic_id: epicId }
            : i,
        );
      });
      api.setItemEpic(itemId, epicId, { cascade: true }).catch(() => reload());
    },
    [reload],
  );

  const createAt = async (parentId: string | null, status: string, position: number) => {
    try {
      // Epic to adopt: a sub-item inherits its parent's epic (falling back to the focused epic when
      // the parent has none); a top-level card adopts the epic the rail is focused on.
      const parent = parentId ? itemsById.get(parentId) : null;
      const epicId = parent ? parent.epic_id ?? focusEpicForNew : focusEpicForNew;
      // A sub-task starts fresh: the board's default ("not started") status + unassigned (no
      // assignee passed), NOT the parent's column. A top-level card takes the column it's made in.
      const created = await api.createItem({
        board_id: boardId!,
        parent_id: parentId,
        title: 'New item',
        status: parentId ? defaultStatus : status,
        position,
        ...(epicId ? { epic_id: epicId } : {}),
      });
      setItems((cur) => [...cur, created]);
      setEditing({ id: created.id, field: 'title' });
      // Surface the new item in the docked details drawer so it's selected and ready to flesh out.
      // Only when docked (a real right rail) — never pop a modal over the board on create. The
      // drawer's title doesn't autofocus while docked, so the inline card-title edit keeps focus.
      if (panel.docked) panel.openItem(created.id);
      record(existenceCommand(created.id, [created.id], created.board_id, `New ${created.key}`));
    } catch {
      reload();
    }
  };

  // Append a new top-level card to a column.
  const addCard = (status: string) => {
    const list = cols[status] ?? [];
    const last = list[list.length - 1];
    createAt(null, status, last ? last.position + 1 : 1000);
  };

  // ---- column (status) management ----
  // Create a new column (status) at the end and drop straight into inline-naming it. The ref
  // guards a fast double-click landing two creates before the busy re-render disables the button.
  const addColumn = async () => {
    if (!boardId || addingColumnRef.current) return;
    addingColumnRef.current = true;
    setStatusBusy(true);
    try {
      const color = STATUS_PALETTE[statuses.length % STATUS_PALETTE.length];
      const created = await api.createStatus(boardId, { label: 'New status', color });
      await reloadStatuses();
      setEditingStatusId(created.id); // its header renders an autofocused name input
    } finally {
      addingColumnRef.current = false;
      setStatusBusy(false);
    }
  };

  // Recolour/rename omit position, so they never write back a stale one over a concurrent reorder.
  const renameColumn = async (status: Status, label: string) => {
    const next = label.trim();
    setEditingStatusId(null);
    if (!next || next === status.label) return;
    setStatusBusy(true);
    try {
      await api.updateStatus(status.id, { label: next, color: status.color });
      await reloadStatuses();
    } finally {
      setStatusBusy(false);
    }
  };

  const recolorColumn = async (status: Status, color: string) => {
    if (color === status.color) return;
    setStatusBusy(true);
    try {
      await api.updateStatus(status.id, { label: status.label, color });
      await reloadStatuses();
    } finally {
      setStatusBusy(false);
    }
  };

  // Delete a column; the server reassigns its items (to Not Started, else the first remaining
  // column). Removing the value bumps `valuesKey`, so the items reload picks up the moves.
  const doDeleteStatus = async () => {
    const status = confirmDelStatus;
    if (!status) return;
    setStatusBusy(true);
    try {
      await api.deleteStatus(status.id);
      setConfirmDelStatus(null);
      await reloadStatuses();
    } finally {
      setStatusBusy(false);
    }
  };

  // Insert a new card as a sibling immediately above/below `ref` (same parent + status).
  const addSibling = (ref: Item, where: 'above' | 'below') => {
    const parentKey = ref.parent_id ?? null;
    const sibs = items.filter((i) => (i.parent_id ?? null) === parentKey).sort((a, b) => a.position - b.position);
    const idx = sibs.findIndex((s) => s.id === ref.id);
    let position: number;
    if (where === 'above') {
      const prev = sibs[idx - 1];
      position = prev ? (prev.position + ref.position) / 2 : ref.position - 1;
    } else {
      const next = sibs[idx + 1];
      position = next ? (ref.position + next.position) / 2 : ref.position + 1;
    }
    createAt(parentKey, ref.status, position);
  };

  // Add a nested child to `parent`, expanding it so the new card is visible, then edit its title.
  const addSub = (parent: Item) => {
    const kids = childrenBy.get(parent.id) ?? [];
    const last = kids[kids.length - 1];
    const n = new Set(expandedRef.current);
    n.add(parent.id);
    setExpanded(n);
    persistExpanded(n);
    createAt(parent.id, parent.status, last ? last.position + 1 : 1000);
  };

  // Detach a nested card to the top level — the menu counterpart to dragging a sub-item out (which
  // is awkward when the parent has many children and cards fill the column, leaving nowhere to
  // drop). It lands as a top-level card in the SAME column it visually sat in (its top-level
  // ancestor's), slotted right after that ancestor, and keeps its own subtree. reparent writes
  // parent_id→null (+position), move writes the column status (+position); one undoable placement.
  const promoteToTop = async (item: Item) => {
    if (!item.parent_id) return; // already top level
    const toStatus = columnOf(item.id) ?? item.status;
    const list = cols[toStatus] ?? [];
    // Walk to the top-level ancestor so the promoted card lands just beneath it (bounded, cycle-safe).
    let anc = item;
    for (let i = 0; i < 10000 && anc.parent_id && itemsById.has(anc.parent_id); i++) anc = itemsById.get(anc.parent_id)!;
    const idx = list.findIndex((i) => i.id === anc.id);
    const next = list[idx + 1];
    const position =
      idx >= 0
        ? next
          ? (anc.position + next.position) / 2
          : anc.position + 1
        : list.length
          ? list[list.length - 1].position + 1
          : 1000;
    const before = { parent_id: item.parent_id, status: item.status, position: item.position };
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, parent_id: null, status: toStatus, position } : i)));
    try {
      await api.reparentItem(item.id, null, position);
      await api.moveItem(item.id, toStatus, position);
      record(placementCommand(item.id, before, { parent_id: null, status: toStatus, position }, item.board_id, `Promote ${item.key}`));
    } finally {
      reload();
    }
  };

  // "Bring to top" / "Send to bottom" from the card menu — reorder a card to the far end of the
  // list it RENDERS in: its column for a top-level card, its parent's children for a sub-item
  // (mirroring cols/childrenBy, so a card promoted out of a hidden epic reorders within its column).
  // A pure position change — moveItem preserves parent_id + status — with placementCommand for undo.
  const reorderExtreme = async (item: Item, edge: 'top' | 'bottom') => {
    const parent = item.parent_id ? itemsById.get(item.parent_id) : undefined;
    const nested = !!parent && !hiddenFromBoard(parent);
    const fullList = nested ? childrenBy.get(parent.id) ?? [] : cols[item.status] ?? [];
    // "Top"/"bottom" are the top/bottom of what the filter SHOWS; the new rank is placed in the gap
    // beside that visible edge (via the full list) so it can't collide with a hidden card. Unfiltered,
    // viewList is a no-op and this reduces to the plain first−1 / last+1.
    const visible = viewList(fullList);
    if (visible.length === 0) return;
    const end = edge === 'top' ? visible[0] : visible[visible.length - 1];
    if (end.id === item.id) return; // already at that visible end — nothing to move
    const full = fullList.filter((i) => i.id !== item.id);
    const position = edge === 'top' ? gapPosition(full, undefined, end) : gapPosition(full, end, undefined);
    const before = { parent_id: item.parent_id ?? null, status: item.status, position: item.position };
    const after = { parent_id: item.parent_id ?? null, status: item.status, position };
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, position } : i)));
    try {
      await api.moveItem(item.id, item.status, position);
      record(placementCommand(item.id, before, after, item.board_id, `${edge === 'top' ? 'Bring to top' : 'Send to bottom'} ${item.key}`));
    } catch {
      reload();
    }
  };

  // Deep-duplicate a card (with its subtree), dropped in right below the original. Guards
  // re-entry (the copy runs a serial batch of creates) and shows the "Saving…" cue meanwhile.
  const duplicate = async (item: Item) => {
    if (duplicatingRef.current) return;
    duplicatingRef.current = true;
    setLoading(true);
    const parentKey = item.parent_id ?? null;
    const sibs = items.filter((i) => (i.parent_id ?? null) === parentKey).sort((a, b) => a.position - b.position);
    const idx = sibs.findIndex((s) => s.id === item.id);
    const next = sibs[idx + 1];
    const position = next ? (item.position + next.position) / 2 : item.position + 1;
    try {
      const created = await duplicateItem(items, item.id, position);
      setItems((cur) => [...cur, ...created]);
      if (created.length) {
        record(
          existenceCommand(created[0].id, created.map((c) => c.id), created[0].board_id, `Duplicate ${item.key}`),
        );
      }
    } catch {
      // A create failed partway through the subtree; reload() below reconciles to server truth.
    } finally {
      duplicatingRef.current = false;
      reload();
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const target = confirmDel;
    const ids = subtreeIds(items, target.id);
    setDeleting(true);
    try {
      const res = await api.deleteItem(target.id);
      setItems((cur) => cur.filter((i) => !ids.has(i.id)));
      setConfirmDel(null);
      record(removalCommand(target.id, res.ids ?? [...ids], target.board_id, `Delete ${target.key}`));
    } catch {
      reload();
    } finally {
      setDeleting(false);
    }
  };

  const toggleExpand = (id: string) => {
    const n = new Set(expandedRef.current);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
    persistExpanded(n);
  };

  // The sortable list a card belongs to: its parent's children (`kids:<id>`) or, for a top-level
  // card, its column's top-level cards (`col:<status>`). Used to place a reordered card as a
  // sibling and to position the blue dwell line. Narrowed to the visible cards (`viewList`) so slot
  // indices line up with the rendered, filtered list and positions land between visible neighbours.
  const listOf = (id: string): { list: Item[]; listKey: string; parentId: string | null } | null => {
    const it = itemsById.get(id);
    if (!it) return null;
    // renderParent (not raw parent_id): a card under a hidden epic renders top-level in its own
    // column, so its list is that column — never the epic's (unrendered, empty) children list, which
    // would make a reorder silently reparent the dragged card under the hidden epic.
    const parent = renderParent(it);
    if (parent) return { list: viewList(childrenBy.get(parent.id) ?? []), listKey: `kids:${parent.id}`, parentId: parent.id };
    return { list: viewList(cols[it.status] ?? []), listKey: `col:${it.status}`, parentId: null };
  };

  // Collision, dwell-aware.
  //
  // DEFAULT (reorder not armed): the drop NESTS. Over a card we return that card's sub-items zone
  // (a non-sortable droppable), so the list stays perfectly still — no cards slide, nothing to
  // hunt — and releasing makes the dragged card a child. An explicit sub-items strip under the
  // pointer wins outright. The physically-inside column decides cross-column and empty columns.
  //
  // ARMED (a 400ms still-dwell completed, see onDragMove): we return the SORTABLE card the reorder
  // is relative to, so dnd-kit slides the list open and the ghost lands as a sibling.
  //
  // Every call also records the hover GEOMETRY (nearest card, whether the pointer is inside a
  // strip, and which list+index a reorder would target) into geomRef for onDragMove's dwell timer.
  const collisionDetection: CollisionDetection = useCallback(
    (a) => {
      const aId = String(a.active.id);
      // The dragged card's own subtree — never a valid drop target (self / cycle).
      const subtree = new Set<string>([aId]);
      const walk = (x: string) =>
        (childrenBy.get(x) ?? []).forEach((k) => {
          if (!subtree.has(k.id)) {
            subtree.add(k.id);
            walk(k.id);
          }
        });
      walk(aId);
      const scoped = a.droppableContainers.filter((c) => {
        const cid = String(c.id);
        if (cid.startsWith('nest:')) return !subtree.has(cid.slice(5));
        if (cid.startsWith('col:')) return true;
        // Any RENDERED card outside the dragged card's own subtree is a valid reorder/nest target —
        // top level OR a nested child of an expanded parent (a collapsed parent's children aren't
        // rendered, so they aren't registered droppables and can't be hit). This is what lets a card
        // drop BETWEEN a parent's expanded subtasks, not only nest under the parent.
        return !subtree.has(cid);
      });

      // Pointer physically inside an explicit sub-items strip → nest into that card.
      const nestZones = scoped.filter((c) => String(c.id).startsWith('nest:'));
      const nestHit = pointerWithin({ ...a, droppableContainers: nestZones });
      hoverNestRef.current = nestHit.length > 0;

      // The column the pointer is physically inside (stable; empty columns still accept a drop).
      const within = pointerWithin({ ...a, droppableContainers: scoped.filter((c) => String(c.id).startsWith('col:')) });
      const withinCol = within.length ? String(within[0].id).slice(4) : null;
      withinColRef.current = withinCol; // published for onDragMove's cross-column highlight

      // The nearest CARD to the pointer (a top-level card, or — for a child drag — a sibling): what we
      // nest under / reorder against. RESTRICTED to the cursor's own column when it's inside one.
      // `closestCorners` ranks by the (wide) drag overlay's corners, so when the overlay straddles two
      // columns it can pick a card in the column the cursor has LEFT — leaving a stale nest/line cue on
      // that card while the drop actually targets the new column. Scoping candidates to `withinCol`
      // keeps the cue in the same column as the cursor. Excludes the dragged card itself.
      const cardConts = scoped.filter((c) => {
        const cid = String(c.id);
        if (cid.startsWith('nest:') || cid.startsWith('col:') || cid === aId) return false;
        return !withinCol || columnOf(cid) === withinCol;
      });
      const cornerCards = closestCorners({ ...a, droppableContainers: cardConts });
      const nearestId = cornerCards.length ? String(cornerCards[0].id) : null;

      // Geometry: which list + slot a reorder would use, and whether the pointer is in a REORDER
      // zone (a card's top/bottom edge band, or the gap above/below it) vs the NEST middle band.
      let geom: { overId: string | null; overNest: boolean; listKey: string | null; index: number; before: boolean; reorder: boolean } = {
        overId: nearestId,
        overNest: nestHit.length > 0,
        listKey: null,
        index: 0,
        before: false,
        reorder: false,
      };
      if (nearestId && a.pointerCoordinates) {
        const rect = a.droppableRects.get(nearestId);
        const info = listOf(nearestId);
        if (rect && info) {
          const dy = a.pointerCoordinates.y - rect.top; // px from the card top (<0 above, >h below)
          const edge = Math.min(REORDER_EDGE_PX, rect.height * 0.4); // cap so short cards keep a nest middle
          const before = dy < rect.height / 2;
          const reorder = dy < edge || dy > rect.height - edge; // near an edge, or in the gap
          const xi = info.list.findIndex((i) => i.id === nearestId);
          geom = { overId: nearestId, overNest: nestHit.length > 0, listKey: info.listKey, index: before ? xi : xi + 1, before, reorder };
        }
      }
      geomRef.current = geom;

      // ---- routing ----
      // A column must be graspable at BOTH ends, independent of which card closestCorners happens to
      // pick — it ranks by the drag overlay's corners, which straddle several cards near an edge, so
      // it can't be trusted to resolve "first" or "last". Judge those two ends by the POINTER against
      // the cards' measured bounds instead: below every rendered card (the empty tail, an expanded
      // parent's subtasks included) → drop LAST; above every rendered card (the header gap or higher)
      // → drop FIRST — both top level, clear of the whole subtree.
      //
      // Resolve the target column by the pointer's X when it has overshot ABOVE the header or BELOW
      // the tail — there pointerWithin(col) fails vertically and `withinCol` is null, yet the further
      // past an end the pointer travels the MORE certainly the intent is first/last, never a nest.
      let endCol = withinCol;
      if (!endCol && a.pointerCoordinates) {
        for (const c of scoped) {
          const cid = String(c.id);
          if (!cid.startsWith('col:')) continue;
          const r = a.droppableRects.get(cid);
          if (r && a.pointerCoordinates.x >= r.left && a.pointerCoordinates.x <= r.left + r.width) {
            endCol = cid.slice(4);
            break;
          }
        }
      }
      if (endCol && a.pointerCoordinates) {
        let minTop = Infinity;
        let maxBottom = -Infinity;
        for (const c of scoped) {
          const cid = String(c.id);
          if (cid.startsWith('nest:') || cid.startsWith('col:') || cid === aId || columnOf(cid) !== endCol) continue;
          const r = a.droppableRects.get(cid);
          if (!r) continue;
          minTop = Math.min(minTop, r.top);
          maxBottom = Math.max(maxBottom, r.top + r.height);
        }
        const tops = viewList(cols[endCol] ?? []).filter((i) => i.id !== aId);
        if (tops.length && maxBottom > -Infinity && a.pointerCoordinates.y > maxBottom) {
          const last = tops[tops.length - 1];
          geom = { overId: last.id, overNest: false, listKey: `col:${endCol}`, index: tops.length, before: false, reorder: true };
          geomRef.current = geom;
          return [{ id: aId }];
        }
        if (tops.length && minTop < Infinity && a.pointerCoordinates.y < minTop) {
          const first = tops[0];
          geom = { overId: first.id, overNest: false, listKey: `col:${endCol}`, index: 0, before: true, reorder: true };
          geomRef.current = geom;
          return [{ id: aId }];
        }
      }
      // Over a strip, or the middle band of a card → NEST (return the card's sub-items zone, a
      // non-sortable droppable, so the list stays put and the drop nests).
      if (nestHit.length) return nestHit;
      // Reorder zone → return the dragged card itself: the cards stay put and the only cue is the
      // blue divider, which onDragMove renders. The drop reads armed/insertion state, not `over`.
      if (geom.reorder && nearestId) return [{ id: aId }];
      if (nearestId) {
        const nz = nestZones.find((c) => String(c.id) === `nest:${nearestId}`);
        // Nest under the nearest card — unless the pointer sits in a DIFFERENT column's space
        // (its empty area / an empty column), which should take a top-level drop instead.
        if (nz && (!withinCol || columnOf(nearestId) === withinCol)) return [{ id: nz.id }];
      }
      if (withinCol) {
        const colC = scoped.find((c) => String(c.id) === `col:${withinCol}`);
        if (colC) return [{ id: colC.id }];
      }
      return cornerCards.length ? cornerCards : closestCorners({ ...a, droppableContainers: scoped });
    },
    // parentOf/columnOf read itemsById; childrenBy/cols derive from `items`; listOf reads shownIds
    // (via viewList) so a filter change must rebuild this to compute slot indices on the visible list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsById, childrenBy, shownIds],
  );

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    setActiveId(id);
    setEditing(null);
    dragClick.current = true; // a drag began — swallow the click its release will emit
    const it = itemsById.get(id);
    dragOrig.current = it ? { status: it.status, position: it.position, parentId: it.parent_id ?? null } : null;
    dragLive.current = null;
    armedRef.current = false;
    overCardRef.current = null;
    insertionRef.current = null;
    hoverNestRef.current = false;
    geomRef.current = null;
    nestTargetRef.current = null;
    dwellLineRef.current = null;
    overColRef.current = null;
    withinColRef.current = null;
    setDwellLine(null);
    setNestTargetId(null);
    setOverColId(null);
  };

  // Sync the drag cues to the current hover geometry (recomputed by collisionDetection each move):
  // a REORDER zone (card edge / gap) shows the blue divider and arms a sibling drop; otherwise the
  // hovered card highlights as the NEST target. No timer — the position alone decides, immediately.
  const onDragMove = (e: DragMoveEvent) => {
    const aId = String(e.active.id);
    const g = geomRef.current;
    let line: { listKey: string; index: number } | null = null;
    let nest: string | null = null;
    if (g && g.overId && g.listKey && itemsById.get(g.overId) && !subtreeIds(items, aId).has(g.overId)) {
      if (!g.overNest && g.reorder) {
        // Suppress the two slots FLANKING the dragged card's own position in this list (index `ai`
        // above it, `ai + 1` below it) — dropping there just puts it back where it started. This
        // mirrors the line suppression in SortableCardList, so we never arm (and later fire a
        // redundant reparent+move on release) for a slot the UI deliberately shows no divider for.
        const aInfo = listOf(aId);
        const ai = aInfo && aInfo.listKey === g.listKey ? aInfo.list.findIndex((i) => i.id === aId) : -1;
        const flanksOwnSlot = ai >= 0 && (g.index === ai || g.index === ai + 1);
        if (!flanksOwnSlot) {
          line = { listKey: g.listKey, index: g.index };
          overCardRef.current = g.overId;
          insertionRef.current = { listKey: g.listKey, index: g.index, overId: g.overId, before: g.before };
        }
        // flanking → leave line/nest null: no cue, no arm, so onDragEnd cleanly reverts the no-op.
      } else {
        nest = g.overId; // sub-items strip, or the card's middle band
      }
    }
    armedRef.current = !!line;
    if (!line) {
      overCardRef.current = null;
      insertionRef.current = null;
    }
    const cur = dwellLineRef.current;
    const sameLine = (!line && !cur) || (!!line && !!cur && line.listKey === cur.listKey && line.index === cur.index);
    if (!sameLine) {
      dwellLineRef.current = line;
      setDwellLine(line);
    }
    if (nestTargetRef.current !== nest) {
      nestTargetRef.current = nest;
      setNestTargetId(nest);
    }
    // Highlight the cursor's column whenever it differs from the card's current column, so a
    // cross-column move is a conscious act. columnOf(aId) is the card's own (top-level ancestor's)
    // column — stable through the drag since items aren't mutated until drop.
    const dest = withinColRef.current;
    const crossCol = dest && dest !== columnOf(aId) ? dest : null;
    if (overColRef.current !== crossCol) {
      overColRef.current = crossCol;
      setOverColId(crossCol);
    }
  };

  // Resolve the drop. If a reorder ARMED (a still-dwell completed), place the card as a sibling of
  // the hovered card. Otherwise the default is to NEST: `over` is a sub-items zone (nest under that
  // card) or a column (land at top level in an empty area). The pick-up snapshot (`dragOrig`) is the
  // "before" for undo and drives whether a reparent is needed.
  const onDragEnd = async (e: DragEndEvent) => {
    const aId = String(e.active.id);
    const orig = dragOrig.current;
    dragOrig.current = null;
    dragLive.current = null;
    const armed = armedRef.current;
    const overCard = overCardRef.current;
    const armedInsertion = insertionRef.current;
    // Clear drag cue state.
    armedRef.current = false;
    overCardRef.current = null;
    insertionRef.current = null;
    geomRef.current = null;
    nestTargetRef.current = null;
    dwellLineRef.current = null;
    overColRef.current = null;
    withinColRef.current = null;
    setDwellLine(null);
    setNestTargetId(null);
    setOverColId(null);
    setActiveId(null);

    const revert = () =>
      orig &&
      setItems((cur) =>
        cur.map((i) => (i.id === aId ? { ...i, status: orig.status, position: orig.position, parent_id: orig.parentId } : i)),
      );
    if (!orig) return revert();

    const liveItem = itemsById.get(aId);
    const placeBefore = { parent_id: orig.parentId, status: orig.status, position: orig.position };
    const recordMove = (after: { parent_id: string | null; status: string; position: number }) => {
      if (liveItem) record(placementCommand(aId, placeBefore, after, liveItem.board_id, `Move ${liveItem.key}`));
    };

    // --- REORDER (armed): place the card as a sibling of the hovered card, on the side the blue
    // line sat. Its list decides the parent + column: a top-level card takes that column; a sibling
    // keeps the dragged card's own status (children render nested regardless). ---
    if (armed && overCard && armedInsertion) {
      const over = itemsById.get(overCard);
      const info = over ? listOf(overCard) : null;
      if (!over || !info || overCard === aId || subtreeIds(items, aId).has(overCard)) return revert();
      const listNoA = info.list.filter((i) => i.id !== aId); // VISIBLE neighbours — decide the slot
      const oi = listNoA.findIndex((i) => i.id === overCard);
      const at = armedInsertion.before ? oi : oi + 1;
      const beforeItem = listNoA[at - 1];
      const afterItem = listNoA[at];
      const parentId = info.parentId;
      const status = parentId ? orig.status : over.status;
      // Rank from the FULL sibling list, so a drop inside a filtered view lands in the gap beside the
      // visible neighbour rather than tying/leapfrogging a hidden card.
      const full = (parentId ? childrenBy.get(parentId) ?? [] : cols[over.status] ?? []).filter((i) => i.id !== aId);
      const position = gapPosition(full, beforeItem, afterItem);
      if (orig.parentId === parentId && orig.status === status && orig.position === position) return revert();
      setItems((cur) => cur.map((i) => (i.id === aId ? { ...i, parent_id: parentId, status, position } : i)));
      try {
        await api.reparentItem(aId, parentId, position);
        await api.moveItem(aId, status, position);
        recordMove({ parent_id: parentId, status, position });
      } finally {
        reload();
      }
      return;
    }

    const over = e.over;
    if (!over) return revert();
    const overId = String(over.id);

    // --- NEST (default): dropped on a card's sub-items zone → make it a child of that card ---
    if (overId.startsWith('nest:')) {
      const targetId = overId.slice(5);
      const target = itemsById.get(targetId);
      if (!target || targetId === aId || subtreeIds(items, aId).has(targetId)) return revert();
      // No-op: dropping a card back onto the parent it already belongs to (nothing would change).
      if (orig.parentId === targetId) return revert();
      // Land after the last VISIBLE child, in the gap beside it (full list) so a nest inside a
      // filtered view slots past the shown children without colliding with a hidden one. When every
      // child is hidden, fall to just past the full last (never a bare 1000, which a hidden child
      // could already occupy).
      const fullKids = (childrenBy.get(targetId) ?? []).filter((k) => k.id !== aId);
      const visKids = viewList(fullKids);
      const last = visKids[visKids.length - 1];
      const position = last
        ? gapPosition(fullKids, last, undefined)
        : fullKids.length
          ? fullKids[fullKids.length - 1].position + 1
          : 1000;
      const nextExpanded = new Set(expandedRef.current);
      nextExpanded.add(targetId);
      setExpanded(nextExpanded);
      persistExpanded(nextExpanded);
      setItems((cur) => cur.map((i) => (i.id === aId ? { ...i, parent_id: targetId, status: orig.status, position } : i)));
      try {
        await api.reparentItem(aId, targetId, position);
        recordMove({ parent_id: targetId, status: orig.status, position });
      } finally {
        reload();
      }
      return;
    }

    // --- top-level placement: dropped on a column's empty area / an empty column ---
    if (overId.startsWith('col:')) {
      const toStatus = overId.slice(4);
      // Against the VISIBLE column list, so a drop into a filtered column's empty area lands after
      // the last shown card — in the gap beside it (full list) rather than behind hidden cards below.
      const fullList = (cols[toStatus] ?? []).filter((i) => i.id !== aId);
      const visList = viewList(fullList);
      const last = visList[visList.length - 1];
      // Empty visible column but hidden cards present → past the full last, never a bare 1000 that a
      // hidden card could already sit on.
      const position = last
        ? gapPosition(fullList, last, undefined)
        : fullList.length
          ? fullList[fullList.length - 1].position + 1
          : 1000;
      const wasChild = orig.parentId !== null;
      if (!wasChild && orig.status === toStatus && viewList(cols[toStatus] ?? []).findIndex((i) => i.id === aId) === visList.length) {
        return revert(); // already last in this (visible) column
      }
      setItems((cur) => cur.map((i) => (i.id === aId ? { ...i, parent_id: null, status: toStatus, position } : i)));
      try {
        if (wasChild) await api.reparentItem(aId, null, position);
        await api.moveItem(aId, toStatus, position);
        recordMove({ parent_id: null, status: toStatus, position });
      } finally {
        reload();
      }
      return;
    }

    return revert();
  };

  const activeItem = activeId ? itemsById.get(activeId) ?? null : null;

  // Copy a shareable link to the item's full-screen page (the card menu's "Copy link to <Type>").
  const copyTaskLink = useCallback(
    (item: Item) => {
      const slug = currentBoard?.slug;
      const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
      if (!slug || !clip) return;
      const url = `${window.location.origin}${taskPath(slug, item.key)}`;
      clip.writeText(url).then(
        () => showToast('Link copied'),
        () => showToast('Couldn’t copy the link', 'error'),
      );
    },
    [currentBoard, showToast],
  );

  if (appLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!currentBoard) return <div className="py-20 text-center text-fg-muted">No board selected.</div>;

  const ctx: CardCtx = {
    statuses,
    metaOf,
    memberMap,
    activeMembers,
    childrenBy,
    cols,
    expanded: searchExpanded,
    matchIds,
    visibleIds: shownIds,
    activeId,
    activeSubtree,
    // A child is hidden unless its parent is expanded, so fold the panel-selection ring to the
    // nearest collapsed ancestor when the open card is tucked away. Uses `searchExpanded` so the
    // fold accounts for the ancestors a live search has force-opened (identity-equal to the user's
    // own expansion when idle).
    highlightId: visibleSelectedId(panel.selectedId, itemsById, (cid) => !searchExpanded.has(cid)),
    editing,
    onEdit: setEditing,
    onSave: saveField,
    onToggle: toggleExpand,
    onOpen: (id, tab) => panel.openItem(id, tab),
    onCopyLink: copyTaskLink,
    onAddSibling: addSibling,
    onAddSub: addSub,
    onPromote: promoteToTop,
    onReorderExtreme: reorderExtreme,
    onDuplicate: duplicate,
    onDelete: (item) => setConfirmDel(item),
    dwellLine,
    nestTargetId,
    onLinkEpic: linkEpic,
    focusedEpicId,
    epicOf,
    epicDragging: draggingEpicId != null,
  };

  const delCount = confirmDel ? subtreeIds(items, confirmDel.id).size - 1 : 0;

  // Preview for the column-delete confirm: how many items sit in it (all levels, not just the
  // top-level cards this column renders) and where they'll land — mirroring the server's rule
  // (Not Started if it survives, else the first remaining column).
  const delStatusCount = confirmDelStatus ? items.filter((i) => i.status === confirmDelStatus.value).length : 0;
  const delStatusTarget = confirmDelStatus
    ? (() => {
        const remaining = statuses.filter((s) => s.id !== confirmDelStatus.id);
        return remaining.find((s) => s.value === 'not_started') ?? remaining[0];
      })()
    : undefined;

  // The board scrolls inside its OWN container (both axes) rather than the page body, so the app
  // header and the pinned FilterBar stay fixed — only the columns move. The columns are a `w-max`
  // grid wider than the viewport; keeping that overflow inside this box (instead of on the body)
  // is what stops the header/filters scrolling sideways with them. The board takes the remaining
  // height (`flex-1`) of a viewport-tall column, so the page fits exactly and the body itself
  // never scrolls. `-mx-6` runs the box edge-to-edge (cancelling the page's px-6) while the grid's
  // own `px-6` carries the columns' side padding, so the scrollbars reach the viewport edges yet
  // the columns still clear them (and line up with the toolbar above). `mt-3` over the FilterBar's
  // `mb-3` gives the board the same breathing room on top as the columns have on the sides.
  // The query container (`container-type:size`) is a NON-scrolling WRAPPER, not the scroll box itself.
  // It lets the trailing "Add column" tile size to the board's VISIBLE height (`100cqh`) without a
  // hardcoded viewport calc. It MUST NOT be on the scroller: size containment on a scroll box whose
  // content sizes to `100cqh` sets up a size<->content feedback loop that JITTERS the wheel/trackpad
  // scroll. The wrapper is the same flex-sized box (`flex-1 min-h-0`), so `100cqh` is unchanged.
  const boardWrapCls = '-mx-6 mt-3 min-h-0 flex-1 [container-type:size]';
  // On mobile the board snaps each column into view as you swipe (scroll-padding matches the row's
  // px-6 so every column lands with the same inset). Disabled while a card is dragged so dnd-kit's
  // edge auto-scroll isn't fought by the snap points, and off entirely on desktop (≥ sm).
  const boardCls = cn(
    'thin-scroll h-full overflow-auto overscroll-contain',
    // Mobile: touch scrolls the board VERTICALLY only (touch-pan-y) so a near-vertical card scroll can't
    // slide sideways into the next column; horizontal column changes go through the deliberate swipe
    // handler above. Desktop keeps native 2-axis scroll; during a drag we drop the constraint so dnd-kit's
    // edge auto-scroll (both axes) works unchanged.
    !activeId && 'snap-x snap-mandatory scroll-pl-6 touch-pan-y sm:snap-none sm:scroll-pl-0 sm:touch-auto',
  );
  // The status columns and the trailing "Add column" tile sit in a flex ROW (not one grid), so the
  // tile can keep its own slim width instead of being forced to a full 480px column track. That
  // matters because the docked details panel reserves ~440px on the right (AppLayout): with a
  // full-width tile a one- or two-column board would push it into the horizontal overflow, off the
  // edge — which is exactly why it seemed to "disappear" when a task was opened. `px-6`/`pr-10`
  // (the trailing gap that lets the last item clear the panel edge) and the `.is-scrolled` header
  // fade live on this wrapper (an ancestor of the columns, so the CSS selector still matches).
  const rowCls = cn(
    'flex w-max items-stretch gap-3 px-6 pb-3',
    scrolled && 'is-scrolled',
    panel.docked && panel.open && 'pr-10',
    // While a card is being dragged, suppress the locked-card hover feedback board-wide: the
    // drop cues (nest ring, dwell line) already show where it will land, and the hover tint on
    // whatever card the pointer passes over just fights them. (Ancestor of every card, so the
    // CSS selector reaches them all.)
    activeId && 'is-dragging',
  );
  // The columns themselves stay a grid so they equalise height to the tallest (grid stretch);
  // `w-max` keeps that grid at content width so it overflows the scroll box rather than squashing.
  // Columns are a viewport-fitting width on mobile (one column + a peek of the next, so you swipe
  // between them) and the fixed 480px track on desktop.
  const colsGridCls = 'grid w-max grid-flow-col auto-cols-[85vw] auto-rows-max gap-3 sm:auto-cols-[480px]';

  return (
    // A near-viewport-tall column (100vh − app header 3.5rem + its 1px border − main's py-6 TOP
    // 1.5rem only): the FilterBar pins at the top and the board fills the rest, so only the board
    // scrolls — never the page body (the 1px is the app header's bottom border, without which the
    // page overflows by it). `-mb-6` cancels main's BOTTOM py-6 (and the height reclaims that 1.5rem)
    // so the board — and its horizontal scrollbar — runs flush to the viewport bottom, the vertical
    // twin of the board's `-mx-6` full-bleed, instead of leaving a 1.5rem strip below the scrollbar.
    <div className="flex flex-col -mb-6" style={{ height: 'calc(100vh - 5rem - 1px)' }}>
      <FilterBar
        filters={filters}
        onChange={setFilters}
        items={items}
        members={members}
        trailing={
          <>
            {loading || statusBusy ? <span className="text-[12px] text-fg-faint">Saving…</span> : null}
            <CollapseExpand
              show={childrenBy.size > 0}
              onCollapseAll={() => {
                const n = new Set<string>();
                setExpanded(n);
                persistExpanded(n);
              }}
              onExpandAll={() => {
                const n = new Set(childrenBy.keys());
                setExpanded(n);
                persistExpanded(n);
              }}
            />
            <SavedViews boardId={currentBoard.id} filters={filters} onApply={setFilters} />
          </>
        }
      />

      {/* One board for both states — no separate filtered render. When a search is active the tree
          is narrowed to matching branches (matches lifted, their parents + siblings dimmed for
          context) via `ctx`; dragging stays on, with the drop math scoped to the visible lists so a
          reorder inside the narrowed view lands between visible neighbours. */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        // Measure droppables continuously so the nest/reorder cues stay accurate as the board
        // scrolls under the pointer — whether from auto-scroll near an edge or a manual scroll
        // gesture mid-drag. (There's no dwell timer or fill to restart anymore, so this no longer
        // causes the churn it once did.) Auto-scroll uses dnd-kit's default edge behavior.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragCancel={() => {
          const orig = dragOrig.current;
          const aId = activeId;
          // Undo any optimistic reorder/detach so an aborted drag leaves the card where it began.
          if (orig && aId) {
            setItems((cur) =>
              cur.map((i) =>
                i.id === aId ? { ...i, status: orig.status, position: orig.position, parent_id: orig.parentId } : i,
              ),
            );
          }
          dragOrig.current = null;
          dragLive.current = null;
          armedRef.current = false;
          overCardRef.current = null;
          insertionRef.current = null;
          geomRef.current = null;
          nestTargetRef.current = null;
          dwellLineRef.current = null;
          overColRef.current = null;
          withinColRef.current = null;
          setDwellLine(null);
          setNestTargetId(null);
          setOverColId(null);
          setActiveId(null);
        }}
        onDragEnd={onDragEnd}
      >
        <div className={boardWrapCls}>
        <div ref={boardRef} onScroll={onBoardScroll} className={boardCls}>
          <div
            className={rowCls}
            // A fresh press clears the guard; a click that arrives while the guard is set (i.e. the
            // one a just-ended drag emits) is swallowed here in the capture phase before it reaches
            // a card's own onClick, so reordering never also opens the drawer.
            onPointerDownCapture={() => {
              dragClick.current = false;
            }}
            onClickCapture={(e) => {
              if (dragClick.current) {
                e.stopPropagation();
                dragClick.current = false;
              }
            }}
          >
            <div className={colsGridCls}>
              {statuses.map((s) => (
                <Column
                  key={s.id}
                  status={s}
                  items={cols[s.value] ?? []}
                  ctx={ctx}
                  onAdd={addCard}
                  matchCount={filteredCountByStatus?.[s.value]}
                  metric={metricByStatus[s.value]}
                  dropTarget={overColId === s.value}
                  editing={editingStatusId === s.id}
                  onStartRename={() => setEditingStatusId(s.id)}
                  onRename={(label) => renameColumn(s, label)}
                  onRecolor={(color) => recolorColumn(s, color)}
                  onDelete={() => setConfirmDelStatus(s)}
                  canDelete={statuses.length > 1}
                />
              ))}
            </div>
            <AddColumn onAdd={addColumn} busy={statusBusy} />
          </div>
        </div>
        </div>
        {/* pointerEvents:none so a wheel/trackpad scroll mid-drag passes THROUGH the overlay to the
            board (the overlay is a fixed, body-portalled element under the cursor; without this the
            wheel targets it and scrolls the page instead of the columns container). The drag itself
            uses document-level listeners, so this doesn't affect it. */}
        <DragOverlay style={{ pointerEvents: 'none' }}>
          {activeItem ? (
            <div className="w-[82vw] rotate-1 sm:w-[464px]">
              <CardShell item={activeItem} ctx={ctx} depth={activeId && parentOf(activeId) ? 1 : 0} overlay />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ItemDrawer
        open={panel.open}
        itemId={panel.itemId}
        boardId={currentBoard.id}
        initialStatus={panel.initialStatus}
        initialType={panel.initialType}
        initialEpicId={panel.initialEpicId ?? null}
        initialTab={panel.initialTab as ItemTab | undefined}
        items={items}
        onClose={panel.close}
        onSaved={reload}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDelete}
        title="Delete item?"
        message={
          <>
            Delete <b className="text-fg-strong">{confirmDel?.title}</b>
            {delCount > 0 ? ` and its ${delCount} sub-item${delCount > 1 ? 's' : ''}` : ''}? This can’t be undone.
          </>
        }
        confirmLabel="Delete"
        busy={deleting}
      />

      <ConfirmDialog
        open={!!confirmDelStatus}
        onClose={() => setConfirmDelStatus(null)}
        onConfirm={doDeleteStatus}
        title="Delete column?"
        message={
          <>
            Delete the <b className="text-fg-strong">{confirmDelStatus?.label}</b> column?
            {delStatusCount > 0 && delStatusTarget ? (
              <>
                {' '}
                Its {delStatusCount} item{delStatusCount > 1 ? 's' : ''} will move to{' '}
                <b className="text-fg-strong">{delStatusTarget.label}</b>.
              </>
            ) : null}
          </>
        }
        confirmLabel="Delete column"
        busy={statusBusy}
      />
    </div>
  );
}

type CardCtx = {
  /** The board's statuses (for the card's status picker + set-status menu). */
  statuses: Status[];
  /** Value → label/colour resolver (with a graceful fallback for unknown values). */
  metaOf: (value: string) => StatusMeta;
  memberMap: Map<string, Member>;
  activeMembers: Member[];
  childrenBy: Map<string, Item[]>;
  /** Top-level cards per column (status value → cards), for the menu's far-end reorder tests. */
  cols: Record<string, Item[]>;
  expanded: Set<string>;
  /** The ids matching the active search/filter, or null when nothing is active. Matching cards are
   *  highlighted (amber ring, full strength); every other rendered card is dimmed for context. */
  matchIds: Set<string> | null;
  /** While searching: the ids that render at all — branch cards (matches + their ancestors) and the
   *  direct children of a branch card (the match's siblings). Non-branch cards are hidden entirely.
   *  Null when nothing is active (everything renders). Card lists filter through this. */
  visibleIds: Set<string> | null;
  /** Id of the card currently being dragged (null when idle) — drives nest-zone drop affordance. */
  activeId: string | null;
  /** The dragged card + its descendants, so a nest zone can disable itself (no self / cycle). */
  activeSubtree: Set<string> | null;
  /** The card id to ring as "open in the details panel" — folded to a collapsed ancestor when
   *  the selected card is hidden inside an unexpanded parent (see visibleSelectedId). */
  highlightId: string | null;
  editing: EditTarget;
  onEdit: (t: EditTarget) => void;
  onSave: (item: Item, patch: Partial<Item>) => void;
  onToggle: (id: string) => void;
  /** Open the item's drawer, optionally deep-linked to a tab (e.g. 'comments'). */
  onOpen: (id: string, tab?: ItemTab) => void;
  /** Copy a shareable link to the item's full-screen page. */
  onCopyLink: (item: Item) => void;
  onAddSibling: (item: Item, where: 'above' | 'below') => void;
  onAddSub: (item: Item) => void;
  /** Detach a nested card to the top level (menu counterpart to dragging a sub-item out). */
  onPromote: (item: Item) => void;
  /** Reorder a card to the far end of its rendered list — its column (top-level) or its parent's
   *  children (sub-item). Drives the menu's "Bring to top" / "Send to bottom". */
  onReorderExtreme: (item: Item, edge: 'top' | 'bottom') => void;
  onDuplicate: (item: Item) => void;
  onDelete: (item: Item) => void;
  /** Where the "reorder here" divider sits mid-drag: a list key (`col:<status>` / `kids:<id>`)
   *  and the index within that list. Null when idle or not in this list. */
  dwellLine: { listKey: string; index: number } | null;
  /** The card the drop would nest INTO — highlighted whole-card while dragging over it (before a
   *  reorder arms). Null when idle, over empty space, or once a reorder is armed. */
  nestTargetId: string | null;
  /** Link an item to an epic (drag from the epic sidebar onto a card). */
  onLinkEpic: (itemId: string, epicId: string) => void;
  /** The epic being highlighted from the sidebar, so cards not linked to it dim. */
  focusedEpicId: string | null;
  /** Resolve an item's epic_id to the epic item (for the coloured chip above the key + title). */
  epicOf: (epicId: string | null) => Item | undefined;
  /** An epic chip is currently being dragged from the sidebar — cards raise a full-bleed drop catcher
   *  so the drop lands (a locked card's `pointer-events:none` content otherwise swallows it in WebKit). */
  epicDragging: boolean;
};

function Column(props: {
  status: Status;
  items: Item[];
  ctx: CardCtx;
  onAdd: (status: string) => void;
  /** While a search or epic-focus filter is active: how many of this column's cards the filter keeps
   *  (drives the amber result badge in the header). Undefined when nothing is filtered — the header
   *  shows the plain full total instead. */
  matchCount?: number;
  /** Live cycle-time metric for this status (avg days its current items have been in it). */
  metric?: StatusMetric;
  /** True while a drag would move the card INTO this column from another one — the whole column is
   *  ringed so a cross-column move reads as deliberate. */
  dropTarget?: boolean;
  /** Filtered view: render static (non-draggable) cards; the column is not a drop target, and
   *  the column-management affordances (recolour / rename / delete) are hidden. */
  readOnly?: boolean;
  /** True while this column's name is being inline-edited (an autofocused header input). */
  editing?: boolean;
  onStartRename?: () => void;
  onRename?: (label: string) => void;
  onRecolor?: (color: string) => void;
  onDelete?: () => void;
  /** False for the last remaining column — a board must always keep at least one. */
  canDelete?: boolean;
}) {
  const { status } = props;
  const manage = !props.readOnly;
  // Called unconditionally (hook rules) even when readOnly; with no DndContext ancestor
  // in the filtered view it simply registers nothing.
  const { setNodeRef, isOver } = useDroppable({ id: `col:${status.value}` });
  // Right-click / long-press an empty part of the column to add a card to it. Cards stop the
  // event first (they show their own menu), so this only fires on the column's own space.
  // Hidden while filtered, matching the suppressed "+" affordance (a new card wouldn't match).
  const cm = useContextMenu();
  return (
    <div
      data-snap-col
      className={cn(
        'flex w-[85vw] shrink-0 snap-start flex-col rounded-card bg-layer transition-shadow sm:w-[480px] sm:snap-align-none',
        // Ring the whole column while its "New item in …" menu is open, so it's clear which
        // column a new card would land in.
        cm.open && 'relative z-10 ring-2 ring-accent',
        // A drag from another column is hovering this one: ring it so the column change is obvious.
        props.dropTarget && 'relative z-10 ring-2 ring-accent/70',
      )}
      {...(props.readOnly ? {} : cm.triggerProps)}
    >
      {/* Header pins to the top of the scrolling board as its cards pass under it. Its rounded
          top corners are painted by .kanban-head (not border-radius) so they survive pinning. */}
      <div className="kanban-head sticky top-0 z-[25] flex items-center justify-between gap-2 px-3 py-2.5">
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {manage ? (
            <ColorPicker
              value={status.color}
              onChange={(c) => props.onRecolor?.(c)}
              trigger={({ toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  onPointerDown={stop}
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-110"
                  aria-label={`Change ${status.label} colour`}
                  title="Change colour"
                >
                  <span className="h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-black/25" style={{ background: status.color }} />
                </button>
              )}
            />
          ) : (
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: status.color }} />
          )}

          {props.editing ? (
            <input
              className={cn(inputCls, 'h-7 py-1 text-[13px] font-semibold')}
              defaultValue={status.label}
              autoFocus
              onFocus={(e) => e.target.select()}
              onPointerDown={stop}
              onBlur={(e) => props.onRename?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  // Revert to the current label, then blur — the onBlur then saves a no-op.
                  (e.target as HTMLInputElement).value = status.label;
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          ) : manage ? (
            <button
              type="button"
              onClick={props.onStartRename}
              className="min-w-0 truncate rounded px-1 py-0.5 text-left text-[13px] font-semibold text-fg-strong transition-colors hover:bg-layer-strong"
              title="Rename column"
            >
              {status.label}
            </button>
          ) : (
            <span className="truncate text-[13px] font-semibold text-fg-strong">{status.label}</span>
          )}
          {/* Idle: the column's full card total. Filtered (search or epic focus): the board is
              narrowed, so the full total no longer matches what's on screen — show the kept count
              instead (an amber pill, or a muted 0 when this column keeps nothing). */}
          {props.matchCount === undefined ? (
            <span className="tnum shrink-0 text-[12px] text-fg-faint">{props.items.length}</span>
          ) : props.matchCount > 0 ? (
            <span className="match-badge shrink-0 rounded-full px-1.5 py-0.5 tnum text-[10px] font-semibold">
              {props.matchCount}
            </span>
          ) : (
            <span className="tnum shrink-0 text-[12px] text-fg-ghost">0</span>
          )}
          {props.metric && props.metric.count > 0 ? (
            <span
              className="shrink-0 whitespace-nowrap text-[11px] text-fg-ghost"
              title={`Average time the ${props.metric.count} item${props.metric.count > 1 ? 's' : ''} currently in “${status.label}” have been in this status`}
            >
              · avg {formatDays(props.metric.avg_dwell_days)}
            </span>
          ) : null}
        </span>

        {manage ? (
          <span className="flex shrink-0 items-center gap-0.5">
            {/* Add-card is hidden while searching (a "New item" wouldn't match, so it'd land
                off-filter and vanish). Column rename/recolour/delete stay — they're orthogonal
                to the item filter, and hiding them was a reason the column controls seemed to
                disappear on search before. */}
            {props.ctx.matchIds ? null : (
              <button
                type="button"
                onClick={() => props.onAdd(status.value)}
                className="tap-target flex h-6 w-6 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
                aria-label={`Add card to ${status.label}`}
                title={`Add card to ${status.label}`}
              >
                <Plus />
              </button>
            )}
            <ColumnMenu
              label={status.label}
              canDelete={props.canDelete ?? true}
              onRename={props.onStartRename}
              onDelete={props.onDelete}
            />
          </span>
        ) : null}
      </div>
      <div
        ref={props.readOnly ? undefined : setNodeRef}
        className={cn(
          // pt-3 (> the insert-button's 10px overhang) keeps the first card's "add above" + clear of the header.
          'flex min-h-[120px] flex-[1_0_auto] flex-col gap-2 rounded-b-card p-2 pt-3 transition-colors',
          !props.readOnly && isOver && 'bg-accent/[0.06]',
        )}
      >
        {props.readOnly ? (
          props.items.map((it) => <CardShell key={it.id} item={it} ctx={props.ctx} depth={0} readOnly />)
        ) : (
          <SortableCardList items={props.items} ctx={props.ctx} depth={0} listKey={`col:${status.value}`} />
        )}
        {props.items.length === 0 ? (
          <div className="rounded-lg bg-layer py-6 text-center text-[12px] text-fg-ghost">
            {props.readOnly ? 'No items' : 'Drop here'}
          </div>
        ) : null}
      </div>

      {!props.readOnly && cm.open ? (
        <ContextMenu
          open
          x={cm.x}
          y={cm.y}
          onClose={cm.close}
          items={[
            {
              label: `New item in ${status.label}`,
              icon: <Plus />,
              onSelect: () => props.onAdd(status.value),
            },
          ]}
        />
      ) : null}
    </div>
  );
}

/** The kebab menu on a kanban column header: rename (drops into inline editing) and delete.
 *  Delete is inert on the last remaining column — a board always keeps at least one status. */
function ColumnMenu(props: { label: string; canDelete: boolean; onRename?: () => void; onDelete?: () => void }) {
  return (
    <Popover
      portal
      align="right"
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          onPointerDown={stop}
          className="tap-target flex h-6 w-6 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
          aria-label={`${props.label} column options`}
          title="Column options"
        >
          <MoreVertical />
        </button>
      )}
    >
      {(close) => (
        <>
          <button
            type="button"
            onClick={() => {
              close();
              props.onRename?.();
            }}
            className={cn(menuRow, 'text-fg-soft')}
          >
            <Pencil className="h-4 w-4" />
            <span className="flex-1">Rename</span>
          </button>
          {props.canDelete ? (
            <button
              type="button"
              onClick={() => {
                close();
                props.onDelete?.();
              }}
              className={cn(menuRow, 'text-danger')}
            >
              <Trash className="h-4 w-4" />
              <span className="flex-1">Delete column</span>
            </button>
          ) : (
            <div className={cn(menuRow, 'text-fg-ghost')} title="A board must keep at least one column">
              <Trash className="h-4 w-4" />
              <span className="flex-1">Delete column</span>
            </div>
          )}
        </>
      )}
    </Popover>
  );
}

/** The trailing "Add column" placeholder — a slim tile after the columns row. Clicking creates a
 *  new status and focuses its inline name editor (handled by the page). It's deliberately narrower
 *  than a status column (240px, not 480px) so the docked details panel doesn't push it off the
 *  right edge; it spans the board's visible height so it reads as an inviting full-height drop zone. */
function AddColumn(props: { onAdd: () => void; busy?: boolean }) {
  return (
    // `sticky top-0` pins the tile to the top of the vertically-scrolling board so it stays reachable
    // no matter how far down the columns you've scrolled. `self-start` opts it out of the row's
    // stretch so its own height wins: `100cqh` (the board's visible height, via the scroll box's
    // `container-type:size`) minus a small gap, so it fills the viewport while leaving a bit of
    // breathing room above the bottom edge rather than butting against it. It still scrolls
    // horizontally with the columns.
    <div data-snap-col className="sticky top-0 z-20 flex h-[calc(100cqh-1.25rem)] w-[240px] shrink-0 snap-start flex-col self-start sm:snap-align-none">
      <button
        type="button"
        onClick={props.onAdd}
        disabled={props.busy}
        className="group/add flex min-h-[120px] flex-1 flex-col items-center justify-center gap-2 rounded-card border-2 border-dashed border-line text-fg-faint transition-colors hover:border-accent/50 hover:bg-layer/50 hover:text-fg-soft disabled:cursor-not-allowed disabled:opacity-60"
        aria-label="Add column"
        title="Add a status column"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-layer text-fg-faint transition-colors group-hover/add:bg-accent/15 group-hover/add:text-accent">
          <Plus />
        </span>
        <span className="text-[13px] font-semibold">Add column</span>
      </button>
    </div>
  );
}

/** The blue "reorder here" divider — a solid glowing line marking exactly where the dragged card
 *  will slot in. Shown immediately whenever the pointer is in a reorder zone. Pointer-inert.
 *  Rendered ABSOLUTELY into the gap at a card's top/bottom edge (see .dwell-line) so it never
 *  displaces the cards — the list stays put and only the line appears. */
function DwellLine({ edge }: { edge: 'top' | 'bottom' }) {
  return (
    <div className={cn('dwell-line', edge === 'top' ? 'dwell-line-top' : 'dwell-line-bottom')} aria-hidden="true">
      <div className="dwell-line-fill" />
    </div>
  );
}

/** A sortable list of cards (a column's top-level cards, or a parent's children). The reorder
 *  divider is not a list item — it's drawn on the adjacent card's edge (lineAbove / lineBelow),
 *  so showing it doesn't bump the cards up or down. */
function SortableCardList(props: { items: Item[]; ctx: CardCtx; depth: number; listKey: string }) {
  const { items, ctx, depth, listKey } = props;
  // While searching, drop cards that aren't on a matching branch (nor a match's sibling) so only
  // the relevant tasks — and their immediate context — render. No-op (same reference) when idle.
  const shown = ctx.visibleIds ? items.filter((i) => ctx.visibleIds!.has(i.id)) : items;
  const dwell = ctx.dwellLine?.listKey === listKey ? ctx.dwellLine : null;
  const rawIdx = dwell ? dwell.index : -1;
  // The dragged card's own placeholder still sits in this list at `ai`; the two slots flanking it
  // (ai = just above it, ai+1 = just below it) drop it right back where it started, so don't draw a
  // line there — you can't place a card above or below itself. (-1 when the card is being dragged
  // into a different list, where every slot is a real move.)
  const ai = ctx.activeId ? shown.findIndex((i) => i.id === ctx.activeId) : -1;
  const lineIdx = ai >= 0 && (rawIdx === ai || rawIdx === ai + 1) ? -1 : rawIdx;
  return (
    <SortableContext items={shown.map((i) => i.id)} strategy={verticalListSortingStrategy}>
      {shown.map((it, i) => (
        <SortableCard
          key={it.id}
          item={it}
          ctx={ctx}
          depth={depth}
          // Slot before this card, or — only for the last card — after it (the end-of-list slot).
          lineAbove={lineIdx === i}
          lineBelow={lineIdx === shown.length && i === shown.length - 1}
        />
      ))}
    </SortableContext>
  );
}

/** A draggable card at any depth: the whole card is the drag handle. Nested (child)
 *  cards stop the pointer-down from reaching the parent card's listeners, so grabbing a
 *  child drags the child — not the whole parent. */
function SortableCard(props: { item: Item; ctx: CardCtx; depth: number; lineAbove?: boolean; lineBelow?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.item.id });
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
  const dragListeners: DragProps['listeners'] = listeners && {
    ...listeners,
    onPointerDown: (e: ReactPointerEvent) => {
      e.stopPropagation();
      listeners.onPointerDown?.(e);
    },
  };
  return (
    <CardShell
      item={props.item}
      ctx={props.ctx}
      depth={props.depth}
      lineAbove={props.lineAbove}
      lineBelow={props.lineBelow}
      drag={{ ref: setNodeRef, style, listeners: dragListeners, attributes, isDragging }}
    />
  );
}

type Sortable = ReturnType<typeof useSortable>;
type DragProps = {
  ref: Sortable['setNodeRef'];
  style: CSSProperties;
  listeners: Sortable['listeners'];
  attributes: Sortable['attributes'];
  isDragging: boolean;
};

/** A backlog card. Top-level cards are draggable; children render as nested cards with
 *  the same controls (insert above/below, open, inline edit) and can expand further. */
function CardShell(props: {
  item: Item;
  ctx: CardCtx;
  depth: number;
  overlay?: boolean;
  readOnly?: boolean;
  /** Draw the reorder divider on this card's top / bottom edge (in the gap) — see DwellLine. */
  lineAbove?: boolean;
  lineBelow?: boolean;
  drag?: DragProps;
}) {
  const { item, ctx, depth, overlay, readOnly, lineAbove, lineBelow, drag } = props;
  const kids = ctx.childrenBy.get(item.id) ?? [];
  const isOpen = ctx.expanded.has(item.id);
  const cm = useContextMenu();
  // Ring the card while its item is the one open in the details panel (never the drag overlay).
  // ctx.highlightId is already folded to a visible ancestor when the open card is collapsed away.
  const isSelected = !overlay && ctx.highlightId === item.id;
  // Search/filter in-place highlight: this card either MATCHES (amber ring, full strength) or is
  // DIMMED for context. `dim` is applied to the card's OWN rows only (header / footer / sub-items
  // toggle), never the wrapper — so a matching child nested inside a dimmed parent still renders at
  // full strength (opacity would otherwise compound onto the descendants). Off in the drag overlay.
  const isMatch = !overlay && !!ctx.matchIds && ctx.matchIds.has(item.id);
  const dim = !overlay && !!ctx.matchIds && !ctx.matchIds.has(item.id);
  // "Locked" = this card is neither the active (selected) one nor being edited. A locked card is a
  // single click-to-select target: its own controls are made inert AND hover-quiet by `.kcard-locked`
  // (CSS) so a stray tap can't fire one and nothing looks directly clickable until the card is
  // active; a soft hover lift + press-in on the card confirm the click. An editing card (e.g. a
  // just-created one, autofocused before it's selected) is NOT locked, so its inline editor is live.
  const locked = !overlay && !isSelected && ctx.editing?.id !== item.id;

  // Epic-sidebar drop target (native HTML5 DnD, orthogonal to the @dnd-kit pointer drag) — only the
  // live card accepts a drop; overlay / read-only copies don't link.
  const epicDrop = useEpicDropTarget(item.id, ctx.onLinkEpic);
  const epicDropOver = !overlay && !readOnly && epicDrop.isOver;
  const epicColor = taskTypeMeta('epic').color;
  const epicItem = ctx.epicOf(item.epic_id);
  // Focus dimming: when an epic is "focused" from the sidebar, rows not linked to it recede
  // (applied to the card's OWN header/footer, not the wrapper, so a linked nested child stays lit).
  const focusDim = !overlay && ctx.focusedEpicId != null && !epicFocusMatches(item.epic_id, ctx.focusedEpicId);

  // Workflow nudge: when this card has sub-items that have ALL converged on one status — and that
  // status differs from the card's own — surface a one-click "Move to {Status} →" chip that advances
  // the parent to match its children. Only a REAL column is offered (a subtask left on a status whose
  // column was deleted is ignored). Read from the FULL child set (ctx.childrenBy), never the
  // search-narrowed view, so it reflects the actual data. TOP-LEVEL only (depth 0): only a card that
  // lives in a column actually "moves to that column" when its status changes — a nested card would
  // just recolour its status dot in place under its parent, so the "Move to …" promise wouldn't hold.
  // Off in the overlay / static read-only copies.
  const moveTarget: Status | null =
    overlay || readOnly || depth !== 0 || kids.length === 0
      ? null
      : (() => {
          const s = kids[0].status;
          if (s === item.status || !kids.every((k) => k.status === s)) return null;
          return ctx.statuses.find((st) => st.value === s) ?? null;
        })();

  // Click/tap anywhere on the card opens details — unless the click landed on an inline
  // control (title editor, status/assignee/date pickers, the hover buttons) or is the click a
  // finished drag emits (swallowed by the board's capture handler). stopPropagation keeps a
  // click on a nested child card from also opening its parent.
  const onCardClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [role="menu"], [role="dialog"]')) return;
    ctx.onOpen(item.id);
  };

  // Two-stage click, so a stray tap can't fire one of the small on-card controls by accident:
  // the FIRST click on a card that ISN'T already the selected one just SELECTS it (opens the
  // drawer). The click is caught here in the CAPTURE phase — before it can reach the control it
  // landed on — so no status/assignee/date picker, insert-+, or toggle activates on that first
  // click. Once the card is selected its controls are live and clicks pass straight through.
  // Scoped to THIS card via the currentTarget check (a click inside a NESTED card is left for that
  // card's own handler), and it only inspects `click`, never pointerdown — so dragging is untouched
  // (a real drag emits no click here; the post-drag click is already swallowed at the board level).
  const onCardClickCapture = (e: ReactMouseEvent) => {
    if (!locked) return; // the active card (or one being edited) — its controls + inline editor are live
    if ((e.target as HTMLElement).closest('.kcard') !== e.currentTarget) return; // a nested card's click
    e.stopPropagation();
    ctx.onOpen(item.id);
  };

  return (
    <div
      ref={drag?.ref}
      style={{ ...(drag?.style ?? {}), ...(epicDropOver ? { boxShadow: `0 0 0 2px ${epicColor}` } : {}) }}
      // `data-item-id` is the scroll target a shared `?item=` link centers on — not on the drag
      // overlay (a floating copy), which must not duplicate the id.
      {...(overlay ? {} : { 'data-item-id': item.id })}
      {...(drag?.attributes ?? {})}
      {...(drag?.listeners ?? {})}
      {...(overlay ? {} : { ...cm.triggerProps, onClick: onCardClick, onClickCapture: onCardClickCapture })}
      {...(overlay || readOnly ? {} : epicDrop.dropProps)}
      className={cn(
        'kcard group/card ctx-target relative select-none rounded-lg p-2.5 transition-colors hover:z-10',
        depth === 0 ? 'bg-surface-raised shadow-sm shadow-black/20' : 'bg-surface-inset',
        !overlay && 'cursor-pointer',
        // Not-yet-active card: click-to-select target (its controls inert + hover-quiet, a press cue).
        locked && 'kcard-locked',
        drag?.isDragging && 'kcard-dragging opacity-40',
        // While its context menu is open, ring the card so you don't lose track of which one
        // you right-clicked — a bolder take on the selected-card glow below. z-10 lifts the ring
        // clear of neighboring cards.
        cm.open && 'z-10 ring-2 ring-accent',
        // Drag-over NEST target: highlight the WHOLE card (not just its sub-items strip) so it's
        // clear a drop here nests under it. Wins over the softer selected glow during a drag.
        !overlay && ctx.nestTargetId === item.id && 'kcard-nest z-10 ring-2 ring-accent',
        // Search/filter match: a warm amber ring lifts it clear of the dimmed surrounding cards.
        isMatch && 'kcard-match z-10',
        // Open in the details panel (and not currently right-clicked): the softer accent glow.
        !cm.open && !(ctx.nestTargetId === item.id) && isSelected && SELECTED_CLS,
        // Showing an edge divider: lift the card so the line (which overflows into the gap) paints
        // clear of a neighbour that might otherwise sit on top (e.g. a selected/hovered z-10 card).
        (lineAbove || lineBelow) && 'z-20',
        // Epic drag hovering over this card (a drop links it) — lift above neighbours (the coloured
        // ring itself is painted via boxShadow so it can carry the epic's own colour).
        epicDropOver && 'z-10',
        // While an epic chip is being dragged, re-enable pointer/drag events on this card's OWN content
        // so the native drop reaches it. A locked (non-selected) card sets its children to
        // pointer-events:none; in WebKit (macOS) that swallows the dragover instead of falling through to
        // this card's drop handler, so the drop silently no-ops. See .kcard-epicdrag in index.scss.
        !overlay && ctx.epicDragging && 'kcard-epicdrag',
        overlay && 'shadow-2xl shadow-black/60 ring-1 ring-accent/50',
      )}
    >
      {/* Reorder divider on this card's edge — absolutely placed in the gap, so it never reflows
          the list. `lineBelow` only ever set on the last card (the end-of-list slot). */}
      {lineAbove ? <DwellLine edge="top" /> : null}
      {lineBelow ? <DwellLine edge="bottom" /> : null}
      {!overlay ? (
        <>
          {/* Insert-sibling creates a card; suppressed while searching (it would land off-filter and
              vanish) and in the overlay/read-only views. */}
          {readOnly || ctx.matchIds ? null : (
            <>
              <InsertButton where="above" onClick={() => ctx.onAddSibling(item, 'above')} />
              <InsertButton where="below" onClick={() => ctx.onAddSibling(item, 'below')} />
            </>
          )}
          {/* open / comments — a hover cluster pinned top-right. */}
          <div className="kcard-hover absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5">
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => ctx.onOpen(item.id, 'comments')}
              className="relative flex h-6 w-6 items-center justify-center rounded-md text-fg-ghost hover:bg-layer-strong hover:text-accent"
              aria-label="Open comments"
              title="Comments"
            >
              <span className="relative inline-flex">
                <Comment className="h-4 w-4" />
                {/* Dot only when the item actually has comments — an at-a-glance "has discussion" cue. */}
                {(item.comment_count ?? 0) > 0 ? (
                  <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-accent" />
                ) : null}
              </span>
            </button>
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => ctx.onOpen(item.id)}
              className="flex h-6 w-6 items-center justify-center rounded-md text-fg-ghost hover:bg-layer-strong hover:text-fg"
              aria-label="Open details"
              title="Open details"
            >
              <OpenPanel className="h-4 w-4" />
            </button>
          </div>
        </>
      ) : null}

      {/* header: [epic chip] · type glyph + key + title (pr clears the top-right hover cluster) */}
      <div className={cn('min-w-0 pr-14', (dim || focusDim) && 'kcard-dim')}>
        {epicItem ? (
          <div className="mb-1 flex">
            <EpicChip epic={epicItem} />
          </div>
        ) : null}
        <div className="flex items-start gap-1">
          {/* Type is the editable JIRA-style glyph right before the key (not duplicated below). */}
          <span className="mt-[1px] shrink-0">
            <TaskTypeDot item={item} ctx={ctx} />
          </span>
          <span className="item-key mt-[3px] shrink-0 rounded px-1.5 py-0.5 tnum text-[10px] font-semibold">
            {item.key}
          </span>
          <TitleCell item={item} ctx={ctx} textCls={depth === 0 ? 'ml-0.5 text-[13px] font-medium text-fg' : 'ml-0.5 text-[12px] text-fg-soft'} />
        </div>
      </div>

      {/* footer: status · assignee · priority · [link/dependency badge] · date */}
      <div className={cn('mt-2 flex items-center gap-1.5', (dim || focusDim) && 'kcard-dim')}>
        <StatusDot item={item} ctx={ctx} />
        <MemberCell item={item} ctx={ctx} size={depth === 0 ? 24 : 20} />
        <PriorityDot item={item} ctx={ctx} />
        <div className="ml-auto flex items-center gap-1.5">
          <LinkBadge item={item} />
          <DateCell item={item} ctx={ctx} />
        </div>
      </div>

      {/* labels: free-form tag chips, shown when present (edited in the drawer / table) */}
      {item.labels?.length ? (
        <div className={cn('mt-1.5 flex flex-wrap gap-1', (dim || focusDim) && 'kcard-dim')}>
          {item.labels.map((l) => (
            <span key={l} className="rounded bg-accent/12 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-accent">
              {l}
            </span>
          ))}
        </div>
      ) : null}

      {/* sub-items: a stable 48px zone that toggles the nested list / adds a first sub-item AND
          is a drop target (`nest:<id>`) so a card dragged onto it becomes a sub-item of this one
          — on EVERY card, empty or not. Static (no droppable) in the overlay + filtered views. */}
      {!overlay && !readOnly ? (
        <SubtasksSection item={item} ctx={ctx} kids={kids} isOpen={isOpen} depth={depth} dim={dim} />
      ) : kids.length ? (
        <div className="mt-2">
          <div className="flex min-h-[48px] items-center gap-1.5 px-1.5 text-[11px] text-fg-faint">
            <Chevron className={cn('h-3 w-3 shrink-0 transition-transform', isOpen && 'rotate-90')} />
            {kids.length} subtask{kids.length > 1 ? 's' : ''}
          </div>
          {isOpen ? (
            <div className="mt-1.5 space-y-2 border-l border-line pl-2">
              {/* Static copies in the overlay / filtered views — no sortable/droppable registration. */}
              {kids.map((k) => (
                <CardShell key={k.id} item={k} ctx={ctx} depth={depth + 1} overlay={overlay} readOnly={readOnly} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* All sub-items have converged on one column that isn't this card's — offer to advance the
          parent to match in one click. Sits at the very bottom, beneath the sub-items, as the last
          thing on the card. Dims with the rest of the card under a search / epic focus. */}
      {moveTarget ? (
        <div className={cn('mt-2', (dim || focusDim) && 'kcard-dim')}>
          <MoveSuggestionChip
            target={moveTarget}
            count={kids.length}
            // Point the way the card will actually move: a target column left of this card's own
            // column arrows left, one to its right arrows right.
            direction={
              ctx.statuses.findIndex((s) => s.value === moveTarget.value) <
              ctx.statuses.findIndex((s) => s.value === item.status)
                ? 'left'
                : 'right'
            }
            onMove={() => ctx.onSave(item, { status: moveTarget.value })}
          />
        </div>
      ) : null}

      {!overlay && cm.open ? (
        <ContextMenu open x={cm.x} y={cm.y} onClose={cm.close} items={buildCardMenu(item, ctx, !!readOnly, depth > 0)} />
      ) : null}
    </div>
  );
}

/** Right-click / long-press menu for a card. In the filtered (read-only) view the
 *  position-based create actions are hidden — a new card can't be placed against a partial
 *  set — matching the hidden insert / "+" affordances there. `nested` (a sub-item, depth > 0)
 *  adds a "Promote to top level" action — the reliable counterpart to dragging it out. */
function buildCardMenu(item: Item, ctx: CardCtx, readOnly: boolean, nested: boolean): MenuEntry[] {
  const open: MenuEntry = {
    label: 'Open details',
    icon: <OpenPanel className="h-4 w-4" />,
    onSelect: () => ctx.onOpen(item.id),
  };
  const copyLink: MenuEntry = {
    label: `Copy link to ${taskTypeMeta(item.type).label}`,
    icon: <ExternalLink className="h-4 w-4" />,
    onSelect: () => ctx.onCopyLink(item),
  };
  const comments: MenuEntry = {
    label: 'Comments',
    icon: <Comment className="h-4 w-4" />,
    onSelect: () => ctx.onOpen(item.id, 'comments'),
  };
  // Reorder to the far end of this card's list — its parent's children (sub-item) or its column
  // (top-level; `nested` is the render depth, so an epic-promoted card counts as top-level here).
  // Narrowed to the VISIBLE cards (ctx.visibleIds), mirroring `reorderExtreme`, so under a filter the
  // far-end tests grey the entry against the shown top/bottom rather than the hidden full extent.
  const rawSiblings = nested ? ctx.childrenBy.get(item.parent_id ?? '') ?? [] : ctx.cols[item.status] ?? [];
  const siblings = ctx.visibleIds ? rawSiblings.filter((i) => ctx.visibleIds!.has(i.id)) : rawSiblings;
  const bringTop: MenuEntry = {
    label: 'Bring to top',
    icon: <ToTop className="h-4 w-4" />,
    disabled: siblings[0]?.id === item.id,
    onSelect: () => ctx.onReorderExtreme(item, 'top'),
  };
  const sendBottom: MenuEntry = {
    label: 'Send to bottom',
    icon: <ToBottom className="h-4 w-4" />,
    disabled: siblings[siblings.length - 1]?.id === item.id,
    onSelect: () => ctx.onReorderExtreme(item, 'bottom'),
  };
  const del: MenuEntry = {
    label: 'Delete',
    icon: <Trash className="h-4 w-4" />,
    danger: true,
    onSelect: () => ctx.onDelete(item),
  };
  // The filtered (read-only) view disables drag/reorder, so it offers no ordering actions.
  if (readOnly) return [open, copyLink, comments, 'separator', del];
  return [
    open,
    copyLink,
    comments,
    'separator',
    bringTop,
    sendBottom,
    'separator',
    { label: 'Add item above', icon: <InsertAbove className="h-4 w-4" />, onSelect: () => ctx.onAddSibling(item, 'above') },
    { label: 'Add item below', icon: <InsertBelow className="h-4 w-4" />, onSelect: () => ctx.onAddSibling(item, 'below') },
    { label: 'Add sub-item', icon: <SubItem className="h-4 w-4" />, onSelect: () => ctx.onAddSub(item) },
    // Only a sub-item can be promoted; a top-level card has nowhere higher to go.
    ...(nested
      ? ([{ label: 'Promote to top level', icon: <Promote className="h-4 w-4" />, onSelect: () => ctx.onPromote(item) }] as MenuEntry[])
      : []),
    'separator',
    { label: 'Duplicate', icon: <Duplicate className="h-4 w-4" />, onSelect: () => ctx.onDuplicate(item) },
    del,
  ];
}

/** Inline-editable title (input on edit, click-to-edit otherwise). */
function TitleCell(props: { item: Item; ctx: CardCtx; textCls: string }) {
  const { item, ctx, textCls } = props;
  if (ctx.editing?.id === item.id && ctx.editing.field === 'title') {
    return (
      <input
        className={cn(inputCls, 'h-7 py-1 text-[13px]')}
        defaultValue={item.title}
        autoFocus
        onFocus={(e) => e.target.select()}
        onPointerDown={stop}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== item.title) ctx.onSave(item, { title: v });
          ctx.onEdit(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') ctx.onEdit(null);
        }}
      />
    );
  }
  return (
    <button
      type="button"
      // Wrap to a second line before ellipsizing (line-clamp-2), rather than trimming at one.
      className={cn('min-w-0 flex-1 line-clamp-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-layer', textCls)}
      onClick={() => ctx.onEdit({ id: item.id, field: 'title' })}
    >
      {item.title}
    </button>
  );
}

function InsertButton(props: { where: 'above' | 'below'; onClick: () => void }) {
  return (
    <button
      type="button"
      onPointerDown={stop}
      onClick={props.onClick}
      className={cn(
        'kcard-hover absolute left-1/2 z-20 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full bg-accent text-black shadow-md shadow-black/40 hover:bg-accent/90',
        props.where === 'above' ? '-top-2.5' : '-bottom-2.5',
      )}
      aria-label={`Add card ${props.where}`}
      title={`Add card ${props.where}`}
    >
      <Plus className="h-3 w-3" />
    </button>
  );
}

/** The bottom "sub-items" section of an interactive card. On EVERY card it is a droppable
 *  (`nest:<id>`) so another card can be dragged onto it to become a sub-item, and it doubles as
 *  the expand/collapse toggle (cards with children) or an "Add Sub-item" button (childless).
 *  The row keeps a stable 48px min-height when collapsed (no layout shift when a drag starts —
 *  the dashed border is always present, just transparent until a drag makes it a live target).
 *  Disabled as a target for the dragged card itself and its own descendants (no self / cycle). */
function SubtasksSection(props: { item: Item; ctx: CardCtx; kids: Item[]; isOpen: boolean; depth: number; dim?: boolean }) {
  const { item, ctx, kids, isOpen, depth, dim } = props;
  const hasKids = kids.length > 0;
  // While searching, only a branch card's children render (a match + its siblings); a dimmed
  // context card keeps its truthful subtask count in the toggle but its subtree stays hidden.
  const hasVisibleKids = ctx.visibleIds ? kids.some((k) => ctx.visibleIds!.has(k.id)) : hasKids;
  const invalid = ctx.activeSubtree?.has(item.id) ?? false;
  const { setNodeRef, isOver } = useDroppable({ id: `nest:${item.id}`, disabled: invalid });
  const dragging = ctx.activeId != null && !invalid;
  const active = isOver && dragging;
  return (
    <div className="mt-2">
      <button
        type="button"
        ref={setNodeRef}
        onPointerDown={stop}
        onClick={() => {
          // Only toggle when there are children this view can actually reveal. During a search a
          // dimmed context card may have children that are all filtered out (hasKids yet
          // !hasVisibleKids) — then the row is inert (a toggle would just spin the chevron over
          // nothing). Childless card → add a first sub-item.
          if (hasVisibleKids) ctx.onToggle(item.id);
          else if (!hasKids) ctx.onAddSub(item);
        }}
        aria-label={hasKids ? `${kids.length} subtask${kids.length > 1 ? 's' : ''}` : 'Add sub-item'}
        title={hasKids ? undefined : 'Add a sub-item, or drop a card here to nest it'}
        className={cn(
          'flex w-full items-center gap-1.5 rounded-md border border-dashed px-1.5 text-left text-[11px] transition-colors',
          // Stable 48px target when collapsed / childless; natural height once expanded.
          isOpen ? 'py-1' : 'min-h-[48px]',
          active
            ? 'border-accent bg-accent/[0.08] text-accent'
            : dragging
              ? 'border-line/40 text-fg-faint hover:border-accent/60 hover:text-fg-soft'
              : 'border-transparent text-fg-faint hover:bg-layer hover:text-fg-soft',
          // Dim this card's OWN sub-items toggle while searching (the nested list below keeps its
          // full strength, so a matching child stays lifted even under a dimmed parent).
          dim && 'kcard-dim',
          // Inert row: children exist but are all filtered out of this search — not a live toggle.
          hasKids && !hasVisibleKids && 'cursor-default',
        )}
      >
        {hasKids ? (
          <>
            <Chevron className={cn('h-3 w-3 shrink-0 transition-transform', isOpen && hasVisibleKids && 'rotate-90')} />
            <span>
              {kids.length} subtask{kids.length > 1 ? 's' : ''}
            </span>
          </>
        ) : (
          <>
            <SubItem className="h-3.5 w-3.5 shrink-0" />
            <span>{active ? 'Drop to nest' : 'Add Sub-item'}</span>
          </>
        )}
      </button>
      {hasVisibleKids && isOpen ? (
        // kcard-subkids: a locked parent makes its OWN controls inert (pointer-events:none) but
        // re-enables this nested list, so sub-cards keep their own click-to-select behaviour.
        <div className="kcard-subkids mt-1.5 space-y-2 border-l border-line pl-2">
          {/* Each parent gets its own sortable list so children reorder within their parent. */}
          <SortableCardList items={kids} ctx={ctx} depth={depth + 1} listKey={`kids:${item.id}`} />
        </div>
      ) : null}
    </div>
  );
}

/** A one-click "Move to {Status} →" nudge, shown on a card whose sub-items have all converged on a
 *  single column that differs from the card's own. Clicking advances the parent to that status — an
 *  ordinary, undoable status change (same path as the status picker), which lands the card in that
 *  column. The target column's colour tints the leading dot; the arrow nudges right on hover. */
function MoveSuggestionChip(props: { target: Status; count: number; direction: 'left' | 'right'; onMove: () => void }) {
  const { target, count, direction, onMove } = props;
  const left = direction === 'left';
  return (
    <button
      type="button"
      onPointerDown={stop}
      onClick={onMove}
      title={`All ${count} sub-item${count > 1 ? 's' : ''} are ${target.label} — move this card to ${target.label}`}
      aria-label={`Move this card to ${target.label}`}
      className="group/move inline-flex max-w-full items-center gap-1.5 rounded-md border border-line/70 bg-layer px-2 py-1 text-[11px] font-medium text-fg-soft transition-colors hover:border-accent/50 hover:bg-accent/10 hover:text-fg"
    >
      {/* The arrow leads (pointing left) or trails (pointing right) so it reads as the direction the
          card moves; the status dot stays beside the label. ArrowRight rotated 180° is the ← form. */}
      {left ? <ArrowRight className="shrink-0 rotate-180 transition-transform group-hover/move:-translate-x-0.5" /> : null}
      <span className="h-2 w-2 shrink-0 rounded-full ring-1 ring-inset ring-black/10" style={{ background: target.color }} />
      <span className="min-w-0 truncate">Move to {target.label}</span>
      {left ? null : <ArrowRight className="shrink-0 transition-transform group-hover/move:translate-x-0.5" />}
    </button>
  );
}

/** Priority chevron on a card, mirroring StatusDot — opens the shared searchable picker. */
function PriorityDot(props: { item: Item; ctx: CardCtx }) {
  const { item, ctx } = props;
  const pm = priorityMeta(item.priority);
  const options: SelectOption[] = PRIORITIES.map((p) => ({
    value: p.value,
    label: p.label,
    leading: <PriorityChevron level={p.level} color={p.color} />,
  }));
  return (
    <SelectPopover
      options={options}
      value={item.priority}
      onChange={(v) => ctx.onSave(item, { priority: v as Priority })}
      searchPlaceholder="Search priority…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target-y flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-layer-strong"
          title={`Priority: ${pm.label}`}
          aria-label={`Priority: ${pm.label}`}
        >
          <PriorityChevron level={pm.level} color={pm.color} />
        </button>
      )}
    />
  );
}

/** Task-type badge on a card, mirroring PriorityDot — opens the shared searchable picker. */
function TaskTypeDot(props: { item: Item; ctx: CardCtx }) {
  const { item, ctx } = props;
  const tm = taskTypeMeta(item.type);
  const options: SelectOption[] = TASK_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    leading: <TaskTypeGlyph type={t.value} color={t.color} />,
  }));
  return (
    <SelectPopover
      options={options}
      value={item.type}
      onChange={(v) => ctx.onSave(item, { type: v as TaskType })}
      searchPlaceholder="Search type…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target-y flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-layer-strong"
          title={`Type: ${tm.label}`}
          aria-label={`Type: ${tm.label}`}
        >
          <TaskTypeGlyph type={tm.value} color={tm.color} />
        </button>
      )}
    />
  );
}

function StatusDot(props: { item: Item; ctx: CardCtx; size?: number }) {
  const { item, ctx } = props;
  const size = props.size ?? 24; // match the assignee avatar on the card
  const sm = ctx.metaOf(item.status);
  const options: SelectOption[] = ctx.statuses.map((s) => ({
    value: s.value,
    label: s.label,
    leading: <StatusSwatch value={s.value} size={16} />,
  }));
  return (
    <SelectPopover
      options={options}
      value={item.status}
      onChange={(v) => ctx.onSave(item, { status: v })}
      searchPlaceholder="Search statuses…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target-y flex shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105"
          title={sm.label}
          aria-label={`Status: ${sm.label}`}
        >
          <StatusSwatch value={item.status} size={size} />
        </button>
      )}
    />
  );
}

function MemberCell(props: { item: Item; ctx: CardCtx; size?: number }) {
  const { item, ctx } = props;
  const size = props.size ?? 24;
  const memberCreate = useMemberCreate();
  const options: SelectOption[] = [
    { value: '', label: 'Unassigned', leading: <MemberAvatar member={undefined} size={20} />, tone: 'text-fg-muted' },
    ...ctx.activeMembers.map((m) => ({
      value: m.id,
      label: m.name,
      leading: <MemberAvatar member={m} size={20} />,
      keywords: m.email ?? '',
    })),
  ];
  return (
    <SelectPopover
      options={options}
      value={item.assignee_id ?? ''}
      onChange={(v) => ctx.onSave(item, { assignee_id: v || null })}
      {...memberCreate((id) => ctx.onSave(item, { assignee_id: id }))}
      searchPlaceholder="Search assignee…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target-y flex shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105"
          aria-label={`Assignee: ${ctx.memberMap.get(item.assignee_id ?? '')?.name ?? 'Unassigned'}. Change assignee`}
        >
          <MemberAvatar member={ctx.memberMap.get(item.assignee_id ?? '')} size={size} />
        </button>
      )}
    />
  );
}

/** End-date: a calendar icon button that opens a custom picker (weekends disabled). */
/** At-a-glance "this item has a linked dependency" cue + a sense of its SIZE: the chain glyph with
 *  the link count, and — when the linked things carry sub-items (a linked epic/tree) — the downstream
 *  item count too, so a link to a big epic reads heavier than a link to a lone task. */
function LinkBadge({ item }: { item: Item }) {
  const links = item.link_count ?? 0;
  if (links <= 0) return null;
  const weight = item.linked_weight ?? 0;
  return (
    <span
      onPointerDown={stop}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
        weight > 0 ? 'bg-accent/12 text-accent' : 'bg-layer-strong text-fg-faint',
      )}
      title={`${links} linked item${links > 1 ? 's' : ''}${weight > 0 ? ` · ${weight} item${weight > 1 ? 's' : ''} across the linked epics/trees` : ''}`}
    >
      <Link className="h-3 w-3" />
      {links}
      {weight > 0 ? <span className="opacity-60">·{weight}</span> : null}
    </span>
  );
}

function DateCell(props: { item: Item; ctx: CardCtx }) {
  const { item, ctx } = props;
  return (
    <span className="relative inline-flex items-center gap-1" onPointerDown={stop}>
      {item.end_date ? <span className="text-[11px] text-fg-faint">{fmtDate(item.end_date)}</span> : null}
      <DatePicker
        value={item.end_date?.slice(0, 10) ?? null}
        onChange={(v) => ctx.onSave(item, { end_date: v })}
        heading="End date"
        align="right"
        trigger={({ toggle }) => (
          <button
            type="button"
            onClick={toggle}
            className={cn(
              'tap-target-y flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-layer-strong hover:text-fg',
              item.end_date ? 'text-fg-faint' : 'text-fg-ghost',
            )}
            title={item.end_date ? `End date: ${fmtDate(item.end_date)}` : 'Set end date'}
            aria-label="Set end date"
          >
            <Calendar />
          </button>
        )}
      />
    </span>
  );
}
